import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { DEFAULT_PRIVACY } from '../lib/privacyDefaults';
import PlanLimitsEditor from '../components/admin/PlanLimitsEditor';
import AiCreditsPanel from '../components/admin/AiCreditsPanel';
import PaymentSettingsPanel from '../components/admin/PaymentSettingsPanel';
import LegalContentEditor from '../components/admin/LegalContentEditor';
import UserAccessDialog from '../components/admin/UserAccessDialog';
import { formatMoney, formatCurrency } from '../lib/currency';
import { Sheet, SheetContent } from '../components/ui/sheet';
import {
  BarChart3, Users, Briefcase, GraduationCap, CreditCard, Settings,
  PoundSterling, TrendingUp, Eye, CheckCircle2, XCircle, Loader2,
  Calendar, Search, MoreVertical, Shield, UserCheck, Ban, Trash2, Edit,
  Plus, ArrowRight, Globe, Building2, Clock, Megaphone, ToggleLeft, ToggleRight, Code,
  Key, Zap, AlertTriangle, RefreshCw, Lock, Unlock, Tag, Percent, FileWarning, UserCog, ShieldCheck, ShieldOff,
  Wifi, WifiOff, ShieldX, MessageSquare, Newspaper, Palette, FileText, BookOpen, Send, Menu, LogOut, Sparkles,
  MapPin, X
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAB_DESCRIPTIONS = {
  overview: 'Platform overview, key metrics and quick actions',
  users: 'Manage user accounts, types and status',
  countries: 'Add, edit and manage countries for selection in job listings and user profiles',
  job_categories: 'Manage job categories, slugs and metadata for job classification',
  course_categories: 'Manage course categories, slugs and metadata for course classification',
  jobs: 'Review and moderate all job listings',
  applications: 'View all job applications and applicant details',
  courses: 'Manage course listings and providers',
  events: 'Create and manage events visible on the platform',
  universities: 'Review, verify and manage university profiles',
  course_providers: 'Review, verify and manage course provider/trainer profiles — kept separate from universities',
  recruitment: 'Direct recruitment service requests',
  student_services: 'Student visa and overseas education applications',
  revenue: 'Revenue analytics across all income streams',
  payments: 'Payment transactions and history',
  subscriptions: 'Subscription plans and pricing tiers',
  advertisements: 'Ad blocks, placements and embed codes',
  payment_gateways: 'Configure and test payment processors, commission, Stripe Connect and service prices',
  ai_credits: 'AI on/off, free job-seeker credits, credit cost per feature and usage',
  moderation: 'Content moderation queue and action log',
  role_management: 'User roles and feature permissions',
  discount_codes: 'Promotional discount codes for subscription plans',
  blog: 'Create, edit and publish blog posts',
  branding: 'Customise platform appearance, logo and contact details',
  legal_pages: 'Edit the Privacy Policy shown on the public site',
  email: 'Email provider status and alert triggers',
  skills_tests: 'Manage skills assessment tests and questions',
  cms: 'Career tips, topic cards and homepage content',
  email_templates: 'Customise the HTML for system-generated emails',
  security: 'CAPTCHA, registration controls and security settings',
  currencies: 'Manage currency codes, symbols and display settings',
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'countries', label: 'Countries', icon: Globe },
  { id: 'job_categories', label: 'Job Categories', icon: Briefcase },
  { id: 'course_categories', label: 'Course Categories', icon: GraduationCap },
  { id: 'jobs', label: 'Jobs', icon: Briefcase },
  { id: 'applications', label: 'Applications', icon: FileText },
  { id: 'courses', label: 'Courses', icon: GraduationCap },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'universities', label: 'Universities', icon: GraduationCap },
  { id: 'course_providers', label: 'Course Providers', icon: Sparkles },
  { id: 'recruitment', label: 'Recruitment', icon: Building2 },
  { id: 'student_services', label: 'Students', icon: Globe },
  { id: 'revenue', label: 'Revenue', icon: PoundSterling },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'subscriptions', label: 'Plans', icon: Settings },
  { id: 'advertisements', label: 'Ads', icon: Megaphone },
  { id: 'currencies', label: 'Currencies', icon: PoundSterling },
  { id: 'payment_gateways', label: 'Gateways', icon: Key },
  { id: 'ai_credits', label: 'AI & Credits', icon: Sparkles },
  { id: 'moderation', label: 'Moderation', icon: FileWarning },
  { id: 'role_management', label: 'Roles', icon: UserCog },
  { id: 'discount_codes', label: 'Discounts', icon: Tag },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'legal_pages', label: 'Privacy Policy', icon: Shield },
  { id: 'email', label: 'Email', icon: Send },
  { id: 'skills_tests', label: 'Skills Tests', icon: CheckCircle2 },
  { id: 'cms', label: 'CMS', icon: Globe },
  { id: 'security', label: 'Security', icon: Shield },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, api, refreshBranding, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [applications, setApplications] = useState([]);
  const [applicationsTotal, setApplicationsTotal] = useState(0);
  const [courses, setCourses] = useState([]);
  const [coursesTotal, setCoursesTotal] = useState(0);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [plans, setPlans] = useState([]);
  const [recruitmentRequests, setRecruitmentRequests] = useState([]);
  const [recruitmentTotal, setRecruitmentTotal] = useState(0);
  const [studentApplications, setStudentApplications] = useState([]);
  const [studentTotal, setStudentTotal] = useState(0);
  const [revenueData, setRevenueData] = useState(null);
  const [ads, setAds] = useState([]);
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState({
    name: '', position: 'home-top', ad_type: 'html', ad_size: '728x90', content: '',
    link_url: '', priority: 10, is_active: true
  });
  const [userFilter, setUserFilter] = useState('all');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const EMPTY_PLAN_FORM = { name: '', slug: '', price: 0, description: '', features: '', billing_cycle: 'monthly', role: 'employer', is_active: true, sort_order: 0, limits: {} };
  const [planForm, setPlanForm] = useState(EMPTY_PLAN_FORM);
  const [planMeta, setPlanMeta] = useState(null);
  const [accessUser, setAccessUser] = useState(null);

  // Payment gateways
  const [gateways, setGateways] = useState([]);
  const [editingGateway, setEditingGateway] = useState(null);
  const [gatewayForm, setGatewayForm] = useState({ public_key: '', secret_key: '', webhook_url: '', transaction_fee_percent: 2.9, transaction_fee_fixed: 0.30 });
  const [testingGateway, setTestingGateway] = useState(null);
  const [gatewayTestResult, setGatewayTestResult] = useState({});

  // Moderation
  const [moderationQueue, setModerationQueue] = useState({ jobs: [], courses: [], reviews: [], events: [], total: 0 });
  const [moderationLogs, setModerationLogs] = useState([]);
  const [moderationFilter, setModerationFilter] = useState('all');
  const [moderatingItem, setModeratingItem] = useState(null);
  const [moderationAction, setModerationAction] = useState('');
  const [moderationReason, setModerationReason] = useState('');
  const [showModerationModal, setShowModerationModal] = useState(false);

  // Role management
  const [permissionsMatrix, setPermissionsMatrix] = useState({});
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [roleChangeReason, setRoleChangeReason] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleUsers, setRoleUsers] = useState([]);

  // Discount codes
  const [discountCodes, setDiscountCodes] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountForm, setDiscountForm] = useState({ code: '', discount_type: 'percent', discount_value: 10, applicable_plans: [], max_uses: '', valid_until: '' });

  // Payment detail modal
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Recruitment detail modal
  const [selectedRecruitment, setSelectedRecruitment] = useState(null);
  const [showRecruitmentModal, setShowRecruitmentModal] = useState(false);

  // Application detail modal
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  // Course detail modal
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);

  // University detail modal
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [showUniversityModal, setShowUniversityModal] = useState(false);
  const [universityReviewNotes, setUniversityReviewNotes] = useState('');

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogComments, setBlogComments] = useState([]);
  const [blogTab, setBlogTab] = useState('posts');
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', content: '', excerpt: '', category: 'general', tags: '', status: 'draft' });
  // Branding state
  const [branding, setBranding] = useState(null);
  const [brandingForm, setBrandingForm] = useState({});
  const [savingBranding, setSavingBranding] = useState(false);
  // Privacy policy editor state
  const [privacyForm, setPrivacyForm] = useState(DEFAULT_PRIVACY);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Email state
  const [emailConfig, setEmailConfig] = useState(null);
  const [sendingDigest, setSendingDigest] = useState('');
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailForm, setTestEmailForm] = useState({ to_email: '', template: 'welcome' });
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [smtpForm, setSmtpForm] = useState({ smtp_host: '', smtp_port: 587, smtp_user: '', smtp_pass: '', from_email: '', from_name: '', has_password: false, source: 'none' });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [clearingSmtp, setClearingSmtp] = useState(false);
  // Skills Tests state
  const [adminTests, setAdminTests] = useState([]);
  const [showTestModal, setShowTestModal] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [testForm, setTestForm] = useState({ title: '', description: '', category: 'general', difficulty: 'intermediate', duration_minutes: 15, passing_score: 70, is_active: true, questions: [] });
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({ question: '', options: ['','','',''], correct_answer: 0, explanation: '' });
  // CMS state
  const [cmsContent, setCmsContent] = useState({});
  const [cmsTab, setCmsTab] = useState('career_tips');
  const [savingCms, setSavingCms] = useState(false);
  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ subject: '', html_body: '', preview_text: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Security / CAPTCHA state
  const [captchaSettings, setCaptchaSettings] = useState({ enabled: true, difficulty: 'medium' });
  const [savingCaptcha, setSavingCaptcha] = useState(false);
  // Bank transfer instructions shown to users at checkout
  const [bankTransferInstructions, setBankTransferInstructions] = useState('');
  const [savingBankTransfer, setSavingBankTransfer] = useState(false);
  // Events state
  const [adminEvents, setAdminEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '', description: '', event_type: 'conference', date: '', end_date: '',
    location: '', is_virtual: false, virtual_link: '', organizer_name: '',
    max_attendees: '', registration_url: '', image_url: '', status: 'active',
    tags: ''
  });
  // Universities state
  const [adminUniversities, setAdminUniversities] = useState([]);
  const [univTotal, setUnivTotal] = useState(0);
  // Partner Universities state (Study Abroad catalogue shown on Student Services page)
  const [partnerUniversities, setPartnerUniversities] = useState([]);
  const [showPartnerUniModal, setShowPartnerUniModal] = useState(false);
  const [editingPartnerUni, setEditingPartnerUni] = useState(null);
  const [partnerUniForm, setPartnerUniForm] = useState({
    name: '', country: 'UK', logo_url: '', website: '', description: '', is_active: true, programs: []
  });
  // Course Providers state — deliberately separate from Universities
  const [adminCourseProviders, setAdminCourseProviders] = useState([]);
  const [selectedCourseProvider, setSelectedCourseProvider] = useState(null);
  const [showCourseProviderModal, setShowCourseProviderModal] = useState(false);
  const [courseProviderReviewNotes, setCourseProviderReviewNotes] = useState('');
  // Ad image upload state
  const [uploadingAdImage, setUploadingAdImage] = useState(false);
  const [seedingAds, setSeedingAds] = useState(false);

  // Countries state
  const [countries, setCountries] = useState([]);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [countryForm, setCountryForm] = useState({ name: '', code: '', description: '', is_active: true });

  // Regions & Cities state (managed per-country from the Countries tab)
  const [showLocationsModal, setShowLocationsModal] = useState(false);
  const [locationsCountry, setLocationsCountry] = useState(null);
  const [regions, setRegions] = useState([]);
  const [citiesByRegion, setCitiesByRegion] = useState({});
  const [newRegionName, setNewRegionName] = useState('');
  const [newCityInputs, setNewCityInputs] = useState({});
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Job/Course Subcategories state (managed per-category from the Categories tabs)
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [subcategoryType, setSubcategoryType] = useState('job'); // 'job' | 'course'
  const [subcategoryParentCategory, setSubcategoryParentCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);

  // Job Categories state
  const [jobCategories, setJobCategories] = useState([]);
  const [showJobCategoryModal, setShowJobCategoryModal] = useState(false);
  const [editingJobCategory, setEditingJobCategory] = useState(null);
  const [jobCategoryForm, setJobCategoryForm] = useState({ name: '', slug: '', description: '', icon: '', is_active: true });

  // Course Categories state
  const [courseCategories, setCourseCategories] = useState([]);
  const [showCourseCategoryModal, setShowCourseCategoryModal] = useState(false);
  const [editingCourseCategory, setEditingCourseCategory] = useState(null);
  const [courseCategoryForm, setCourseCategoryForm] = useState({ name: '', slug: '', description: '', icon: '', is_active: true });

  // User edit modal
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [editingEditUser, setEditingEditUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({ full_name: '', email: '', phone: '', location: '', bio: '' });

  // Currencies state
  const [currencies, setCurrencies] = useState([]);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [currencyForm, setCurrencyForm] = useState({ code: '', name: '', symbol: '', position: 'before', decimal_places: 2, is_active: true });

  useEffect(() => {
    if (user?.user_type !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [activeTab, userFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview' || !stats) {
        const res = await api.get('/admin/stats');
        setStats(res.data);
      }
      if (activeTab === 'users') {
        const params = userFilter !== 'all' ? `?user_type=${userFilter}` : '';
        const res = await api.get(`/admin/users${params}`);
        setUsers(res.data.users);
        setUsersTotal(res.data.total);
      }
      if (activeTab === 'jobs') {
        const res = await api.get('/admin/jobs');
        setJobs(res.data.jobs);
        setJobsTotal(res.data.total);
      }
      if (activeTab === 'applications') {
        const res = await api.get('/admin/applications');
        setApplications(res.data.applications);
        setApplicationsTotal(res.data.total);
      }
      if (activeTab === 'courses') {
        const res = await api.get('/admin/courses');
        setCourses(res.data.courses);
        setCoursesTotal(res.data.total);
      }
      if (activeTab === 'payments') {
        const res = await api.get('/admin/payments');
        setPayments(res.data.payments);
        setPaymentsTotal(res.data.total);
        setTotalRevenue(res.data.total_revenue);
        try {
          const btRes = await api.get('/payments/bank-transfer/details');
          setBankTransferInstructions(btRes.data.instructions || '');
        } catch { /* keep current value */ }
      }
      if (activeTab === 'subscriptions') {
        const res = await api.get('/admin/subscription-plans');
        setPlans(res.data);
        try { setPlanMeta((await api.get('/admin/subscription-plans/meta')).data); } catch { /* editor falls back to defaults */ }
      }
      if (activeTab === 'recruitment') {
        const res = await api.get('/admin/recruitment-requests');
        setRecruitmentRequests(res.data.requests || []);
        setRecruitmentTotal(res.data.total || 0);
      }
      if (activeTab === 'student_services') {
        const res = await api.get('/admin/student-applications');
        setStudentApplications(res.data.applications || []);
        setStudentTotal(res.data.total || 0);
        const uniRes = await api.get('/admin/partner-universities');
        setPartnerUniversities(uniRes.data.universities || []);
      }
      if (activeTab === 'revenue') {
        const res = await api.get('/admin/revenue-analytics');
        setRevenueData(res.data);
      }
      if (activeTab === 'advertisements') {
        const res = await api.get('/admin/ads');
        setAds(res.data.ads || []);
      }
      if (activeTab === 'payment_gateways') {
        try {
          const res = await api.get('/admin/payment-gateways');
          setGateways(res.data);
        } catch { setGateways([]); }
      }
      if (activeTab === 'moderation') {
        try {
          const [queueRes, logsRes] = await Promise.all([
            api.get('/admin/moderation/queue'),
            api.get('/admin/moderation/logs')
          ]);
          setModerationQueue(queueRes.data);
          setModerationLogs(logsRes.data);
        } catch { }
      }
      if (activeTab === 'role_management') {
        try {
          const [usersRes, matrixRes] = await Promise.all([
            api.get('/admin/users?limit=200'),
            api.get('/admin/roles/permissions-matrix')
          ]);
          setRoleUsers(usersRes.data.users || []);
          setPermissionsMatrix(matrixRes.data);
        } catch { }
      }
      if (activeTab === 'discount_codes') {
        try {
          const res = await api.get('/admin/discount-codes');
          setDiscountCodes(res.data);
        } catch { setDiscountCodes([]); }
      }
      if (activeTab === 'blog') {
        try {
          const [postsRes, commentsRes] = await Promise.all([api.get('/admin/blog/posts'), api.get('/admin/blog/comments')]);
          setBlogPosts(postsRes.data.posts || []);
          setBlogComments(commentsRes.data || []);
        } catch { setBlogPosts([]); setBlogComments([]); }
      }
      if (activeTab === 'branding') {
        try {
          const res = await api.get('/admin/branding');
          setBranding(res.data);
          setBrandingForm(res.data);
        } catch { console.error('Failed to load branding'); }
      }
      if (activeTab === 'legal_pages') {
        try {
          const res = await api.get('/pages/privacy');
          setPrivacyForm(Array.isArray(res.data?.sections)
            ? { title: res.data.title || DEFAULT_PRIVACY.title, last_updated: res.data.last_updated || '', sections: res.data.sections }
            : DEFAULT_PRIVACY);
        } catch { setPrivacyForm(DEFAULT_PRIVACY); }
      }
      if (activeTab === 'email') {
        try {
          const res = await api.get('/admin/notifications/email-config');
          setEmailConfig(res.data);
        } catch { setEmailConfig(null); }
        try {
          const res = await api.get('/admin/settings/smtp');
          setSmtpForm(f => ({ ...f, ...res.data, smtp_pass: '' }));
        } catch { /* leave defaults */ }
      }
      if (activeTab === 'skills_tests') {
        try {
          const res = await api.get('/admin/skills-tests');
          setAdminTests(res.data || []);
        } catch { setAdminTests([]); }
      }
      if (activeTab === 'cms') {
        try {
          const [tipsRes, topicsRes, homepageRes] = await Promise.all([
            api.get('/admin/content/career_tips'),
            api.get('/admin/content/career_topics'),
            api.get('/admin/content/homepage'),
          ]);
          setCmsContent({ career_tips: tipsRes.data, career_topics: topicsRes.data, homepage: homepageRes.data });
        } catch { setCmsContent({}); }
      }
      if (activeTab === 'email_templates') {
        try {
          const res = await api.get('/admin/email-templates');
          setEmailTemplates(res.data || []);
        } catch { setEmailTemplates([]); }
      }
      if (activeTab === 'security') {
        try {
          const res = await api.get('/admin/settings/captcha');
          setCaptchaSettings(res.data);
        } catch { /* keep defaults */ }
      }
      if (activeTab === 'events') {
        try {
          const res = await api.get('/admin/events');
          setAdminEvents(res.data.events || []);
        } catch { setAdminEvents([]); }
      }
      if (activeTab === 'universities') {
        try {
          const [res, partnerRes] = await Promise.all([
            api.get('/admin/universities'),
            api.get('/admin/partner-universities'),
          ]);
          setAdminUniversities(res.data.universities || []);
          setUnivTotal(res.data.total || 0);
          setPartnerUniversities(partnerRes.data.universities || []);
        } catch { setAdminUniversities([]); }
      }
      if (activeTab === 'course_providers') {
        try {
          const res = await api.get('/admin/course-providers');
          setAdminCourseProviders(res.data.providers || []);
        } catch { setAdminCourseProviders([]); }
      }
      if (activeTab === 'countries' || activeTab === 'users') {
        try {
          const res = await api.get('/admin/countries');
          setCountries(res.data.countries || []);
        } catch { setCountries([]); }
      }
      if (activeTab === 'job_categories') {
        try {
          const res = await api.get('/admin/job-categories');
          setJobCategories(res.data.categories || []);
        } catch { setJobCategories([]); }
      }
      if (activeTab === 'course_categories') {
        try {
          const res = await api.get('/admin/course-categories');
          setCourseCategories(res.data.categories || []);
        } catch { setCourseCategories([]); }
      }
      if (activeTab === 'currencies') {
        try {
          const res = await api.get('/admin/currencies');
          setCurrencies(res.data.currencies || []);
        } catch { setCurrencies([]); }
      }
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally { setLoading(false); }
  };
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleUser = async (userId, isActive) => {
    try {
      await api.put(`/admin/users/${userId}/status?is_active=${isActive}`);
      toast.success(isActive ? 'User activated' : 'User deactivated');
      fetchData();
    } catch { toast.error('Failed to update user'); }
  };

  const verifyUser = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/verify`);
      toast.success('User verified');
      fetchData();
    } catch { toast.error('Failed to verify user'); }
  };

  const approvePayment = async (paymentId) => {
    if (!window.confirm('Mark this payment as paid and activate the plan?')) return;
    try {
      await api.put(`/admin/payments/${paymentId}/approve`);
      toast.success('Payment approved');
      setShowPaymentModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve payment'); }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment record? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/payments/${paymentId}`);
      toast.success('Payment deleted');
      setShowPaymentModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete payment'); }
  };

  const saveBankTransferInstructions = async () => {
    setSavingBankTransfer(true);
    try {
      await api.put('/admin/settings/bank-transfer-details', { instructions: bankTransferInstructions });
      toast.success('Bank transfer details saved');
    } catch { toast.error('Failed to save bank transfer details'); }
    finally { setSavingBankTransfer(false); }
  };

  const sendPaymentReminder = async (paymentId) => {
    try {
      await api.post(`/admin/payments/${paymentId}/send-reminder`);
      toast.success('Reminder email sent');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send reminder'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user account? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const updateJobStatus = async (jobId, status) => {
    try {
      await api.put(`/admin/jobs/${jobId}/status?status=${status}`);
      toast.success('Job status updated');
      fetchData();
    } catch { toast.error('Failed to update job'); }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await api.delete(`/admin/jobs/${jobId}`);
      toast.success('Job deleted');
      fetchData();
    } catch { toast.error('Failed to delete job'); }
  };

  const updateCourseStatus = async (courseId, status) => {
    try {
      await api.put(`/admin/courses/${courseId}/status?status=${status}`);
      toast.success('Course status updated');
      fetchData();
    } catch { toast.error('Failed to update course'); }
  };

  const savePlan = async () => {
    const planData = { ...planForm, features: planForm.features.split('\n').filter(f => f.trim()), price: parseFloat(planForm.price) };
    try {
      if (editingPlan) {
        await api.put(`/admin/subscription-plans/${editingPlan.id}`, planData);
        toast.success('Plan updated');
      } else {
        await api.post('/admin/subscription-plans', planData);
        toast.success('Plan created');
      }
      setShowPlanModal(false);
      setEditingPlan(null);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save plan'); }
  };

  // ---------- Events handlers ----------
  const openNewEvent = () => {
    setEditingEvent(null);
    setEventForm({ title: '', description: '', event_type: 'conference', date: '', end_date: '', location: '', is_virtual: false, virtual_link: '', organizer_name: '', max_attendees: '', registration_url: '', image_url: '', status: 'active', tags: '' });
    setShowEventModal(true);
  };
  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    setEventForm({
      title: ev.title || '', description: ev.description || '',
      event_type: ev.event_type || 'conference',
      date: ev.date ? ev.date.slice(0, 16) : '',
      end_date: ev.end_date ? ev.end_date.slice(0, 16) : '',
      location: ev.location || '', is_virtual: ev.is_virtual || false,
      virtual_link: ev.virtual_link || '', organizer_name: ev.organizer_name || '',
      max_attendees: ev.max_attendees || '', registration_url: ev.registration_url || '',
      image_url: ev.image_url || '', status: ev.status || 'active',
      tags: (ev.tags || []).join(', ')
    });
    setShowEventModal(true);
  };
  const saveEvent = async () => {
    try {
      const payload = {
        ...eventForm,
        max_attendees: eventForm.max_attendees ? parseInt(eventForm.max_attendees) : null,
        tags: eventForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      if (editingEvent) {
        await api.put(`/admin/events/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/admin/events', payload);
        toast.success('Event created');
      }
      setShowEventModal(false); setEditingEvent(null); fetchData();
    } catch { toast.error('Failed to save event'); }
  };
  const deleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try { await api.delete(`/admin/events/${eventId}`); toast.success('Event deleted'); fetchData(); }
    catch { toast.error('Failed to delete event'); }
  };
  const setEventStatus = async (eventId, status) => {
    try { await api.put(`/admin/events/${eventId}/status`, { status }); toast.success('Status updated'); fetchData(); }
    catch { toast.error('Failed to update status'); }
  };

  // ---------- Universities handlers ----------
  const verifyUniversity = async (univId) => {
    try { await api.put(`/admin/universities/${univId}/verify`); toast.success('University verified'); fetchData(); }
    catch { toast.error('Failed to verify'); }
  };
  const toggleUniversity = async (univId, isActive) => {
    try { await api.put(`/admin/universities/${univId}/status?is_active=${!isActive}`); toast.success(isActive ? 'Deactivated' : 'Activated'); fetchData(); }
    catch { toast.error('Failed to update status'); }
  };
  const deleteUniversity = async (univId) => {
    if (!window.confirm('Delete this university? This cannot be undone.')) return;
    try { await api.delete(`/admin/universities/${univId}`); toast.success('University deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };
  const reviewUniversity = async (univId, decision) => {
    try {
      await api.put(`/admin/universities/${univId}/review`, { decision, notes: universityReviewNotes });
      toast.success(decision === 'approve' ? 'University approved' : 'University rejected');
      fetchData();
    } catch { toast.error('Failed to submit review'); }
  };

  // ---------- Course Provider handlers (deliberately separate from Universities) ----------
  const verifyCourseProvider = async (providerId) => {
    try { await api.put(`/admin/course-providers/${providerId}/verify`); toast.success('Course provider verified'); fetchData(); }
    catch { toast.error('Failed to verify'); }
  };
  const toggleCourseProvider = async (providerId, isActive) => {
    try { await api.put(`/admin/course-providers/${providerId}/status?is_active=${!isActive}`); toast.success(isActive ? 'Deactivated' : 'Activated'); fetchData(); }
    catch { toast.error('Failed to update status'); }
  };
  const deleteCourseProvider = async (providerId) => {
    if (!window.confirm('Delete this course provider? This cannot be undone.')) return;
    try { await api.delete(`/admin/course-providers/${providerId}`); toast.success('Course provider deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };
  const reviewCourseProvider = async (providerId, decision) => {
    try {
      await api.put(`/admin/course-providers/${providerId}/review`, { decision, notes: courseProviderReviewNotes });
      toast.success(decision === 'approve' ? 'Course provider approved' : 'Course provider rejected');
      fetchData();
    } catch { toast.error('Failed to submit review'); }
  };

  // ---------- Ad block handlers ----------
  const AD_POSITIONS = [
    // Global
    { value: 'header', label: 'Global — Header (below navbar)', size: '728×90' },
    { value: 'footer', label: 'Global — Footer (above footer)', size: '728×90' },
    { value: 'between-content', label: 'Global — Between Content Sections', size: '468×60' },
    // Home
    { value: 'home-top', label: 'Home — Top Leaderboard', size: '728×90' },
    { value: 'home-middle', label: 'Home — Mid Rectangle', size: '300×250' },
    { value: 'home-bottom', label: 'Home — Bottom Banner', size: '728×90' },
    // Jobs
    { value: 'jobs-top', label: 'Jobs Page — Top Leaderboard', size: '728×90' },
    { value: 'jobs-sidebar', label: 'Jobs Page — Sidebar', size: '300×600' },
    // Courses
    { value: 'courses-top', label: 'Courses Page — Top Leaderboard', size: '728×90' },
    { value: 'courses-sidebar', label: 'Courses Page — Sidebar', size: '300×250' },
    // Events
    { value: 'events-top', label: 'Events Page — Top Leaderboard', size: '728×90' },
    // Universities
    { value: 'universities-top', label: 'Universities Page — Top Leaderboard', size: '728×90' },
    // Blog
    { value: 'blog-top', label: 'Blog — Top Leaderboard', size: '728×90' },
    { value: 'blog-sidebar', label: 'Blog — Sidebar', size: '300×250' },
    // Detail pages
    { value: 'job-detail-sidebar', label: 'Job Detail — Sidebar', size: '300×250' },
    { value: 'course-detail-sidebar', label: 'Course Detail — Sidebar', size: '300×250' },
  ];

  const AD_SIZES = [
    { value: '728x90', label: 'Leaderboard — 728×90' },
    { value: '300x250', label: 'Medium Rectangle — 300×250' },
    { value: '300x600', label: 'Half Page — 300×600' },
    { value: '160x600', label: 'Wide Skyscraper — 160×600' },
    { value: '468x60', label: 'Full Banner — 468×60' },
    { value: '336x280', label: 'Large Rectangle — 336×280' },
    { value: '320x50', label: 'Mobile Banner — 320×50' },
    { value: '320x100', label: 'Large Mobile Banner — 320×100' },
    { value: 'responsive', label: 'Responsive (100% width)' },
  ];

  const AD_TYPES = [
    { value: 'html', label: 'Custom HTML / Embed' },
    { value: 'adsense', label: 'Google AdSense' },
    { value: 'image', label: 'Image Banner' },
    { value: 'text', label: 'Text Banner' },
  ];

  const uploadAdImage = async (file) => {
    if (!file) return;
    setUploadingAdImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Do NOT set Content-Type manually — axios must auto-set multipart/form-data with the boundary
      const res = await api.post('/admin/ads/upload-image', formData);
      setAdForm(f => ({ ...f, content: res.data.url }));
      toast.success('Image uploaded');
    } catch { toast.error('Image upload failed'); }
    finally { setUploadingAdImage(false); }
  };

  const seedPlaceholderAds = async () => {
    setSeedingAds(true);
    try {
      const res = await api.post('/admin/ads/seed-placeholders');
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Failed to seed placeholder ads'); }
    finally { setSeedingAds(false); }
  };

  const saveAd = async () => {
    try {
      const payload = { ...adForm, priority: parseInt(adForm.priority) || 10 };
      if (editingAd) {
        await api.put(`/admin/ads/${editingAd.id}`, payload);
        toast.success('Ad updated');
      } else {
        await api.post('/admin/ads', payload);
        toast.success('Ad created');
      }
      setShowAdModal(false);
      setEditingAd(null);
      fetchData();
    } catch { toast.error('Failed to save ad'); }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Delete this ad block?')) return;
    try {
      await api.delete(`/admin/ads/${adId}`);
      toast.success('Ad deleted');
      fetchData();
    } catch { toast.error('Failed to delete ad'); }
  };

  const toggleAd = async (adId) => {
    try {
      await api.put(`/admin/ads/${adId}/toggle`);
      toast.success('Ad toggled');
      fetchData();
    } catch { toast.error('Failed to toggle ad'); }
  };

  const openEditAd = (ad) => {
    setEditingAd(ad);
    setAdForm({
      name: ad.name || '',
      position: ad.position || 'home-top',
      ad_type: ad.ad_type || 'html',
      ad_size: ad.ad_size || '728x90',
      content: ad.content || '',
      link_url: ad.link_url || '',
      priority: ad.priority ?? 10,
      is_active: ad.is_active !== false,
    });
    setShowAdModal(true);
  };

  const openNewAd = () => {
    setEditingAd(null);
    setAdForm({ name: '', position: 'home-top', ad_type: 'html', ad_size: '728x90', content: '', link_url: '', priority: 10, is_active: true });
    setShowAdModal(true);
  };
  // ---------- End ad handlers ----------

  // -------- Gateway handlers --------
  const testGateway = async (gatewayId) => {
    setTestingGateway(gatewayId);
    try {
      const res = await api.post(`/admin/payment-gateways/${gatewayId}/test`);
      setGatewayTestResult(prev => ({ ...prev, [gatewayId]: res.data }));
      if (res.data.status === 'connected') toast.success(`${res.data.gateway}: ${res.data.message}`);
      else toast.error(`${res.data.gateway}: ${res.data.message || 'Not connected'}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Test failed'); }
    finally { setTestingGateway(null); }
  };

  const toggleGateway = async (gateway) => {
    try {
      await api.put(`/admin/payment-gateways/${gateway.id}`, { is_enabled: !gateway.is_enabled });
      toast.success(`${gateway.display_name} ${!gateway.is_enabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update gateway'); }
  };

  const saveGateway = async () => {
    if (!editingGateway) return;
    const payload = {};
    if (gatewayForm.public_key) payload.public_key = gatewayForm.public_key;
    if (gatewayForm.secret_key) payload.secret_key = gatewayForm.secret_key;
    if (gatewayForm.webhook_url) payload.webhook_url = gatewayForm.webhook_url;
    if (gatewayForm.webhook_secret) payload.webhook_secret = gatewayForm.webhook_secret;
    if (gatewayForm.transaction_fee_percent) payload.transaction_fee_percent = parseFloat(gatewayForm.transaction_fee_percent);
    if (gatewayForm.transaction_fee_fixed) payload.transaction_fee_fixed = parseFloat(gatewayForm.transaction_fee_fixed);
    try {
      await api.put(`/admin/payment-gateways/${editingGateway.id}`, payload);
      toast.success('Gateway updated');
      setEditingGateway(null);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save gateway'); }
  };

  // -------- Moderation handlers --------
  const openModerateModal = (item, type, action) => {
    setModeratingItem({ ...item, content_type: type });
    setModerationAction(action);
    setModerationReason('');
    setShowModerationModal(true);
  };

  const submitModerationAction = async () => {
    if (!moderatingItem) return;
    try {
      await api.post('/admin/moderation/action', {
        target_type: moderatingItem.content_type,
        target_id: moderatingItem.id,
        action: moderationAction,
        reason: moderationReason
      });
      toast.success(`Content ${moderationAction}d`);
      setShowModerationModal(false);
      fetchData();
    } catch { toast.error('Moderation action failed'); }
  };

  // -------- Role handlers --------
  const openRoleModal = (user) => {
    setEditingUserRole(user);
    setNewRole(user.user_type);
    setRoleChangeReason('');
    setShowRoleModal(true);
  };

  const saveRoleChange = async () => {
    if (!editingUserRole) return;
    try {
      await api.put(`/admin/users/${editingUserRole.id}/role`, { user_type: newRole, reason: roleChangeReason });
      toast.success(`Role updated to ${newRole}`);
      setShowRoleModal(false);
      fetchData();
    } catch { toast.error('Failed to update role'); }
  };

  const suspendUser = async (userId, isSuspended) => {
    try {
      if (isSuspended) {
        await api.post(`/admin/users/${userId}/unsuspend`);
        toast.success('User unsuspended');
      } else {
        await api.post(`/admin/users/${userId}/suspend`, { reason: 'Suspended by admin' });
        toast.success('User suspended');
      }
      fetchData();
    } catch { toast.error('Action failed'); }
  };

  // -------- Discount handlers --------
  const saveDiscountCode = async () => {
    const payload = {
      ...discountForm,
      discount_value: parseFloat(discountForm.discount_value),
      max_uses: discountForm.max_uses ? parseInt(discountForm.max_uses) : null,
      valid_until: discountForm.valid_until || null
    };
    try {
      await api.post('/admin/discount-codes', payload);
      toast.success('Discount code created');
      setShowDiscountModal(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create code'); }
  };

  const toggleDiscountCode = async (code) => {
    try {
      const dc = discountCodes.find(d => d.code === code);
      await api.put(`/admin/discount-codes/${code}`, { is_active: !dc?.is_active });
      toast.success('Code toggled');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle code'); }
  };

  const deleteDiscountCode = async (code) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await api.delete(`/admin/discount-codes/${code}`);
      toast.success('Code deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete code'); }
  };

  // Countries handlers
  const saveCountry = async () => {
    if (!countryForm.name || !countryForm.code) {
      toast.error('Name and code are required');
      return;
    }
    try {
      if (editingCountry) {
        await api.put(`/admin/countries/${editingCountry.id}`, countryForm);
        toast.success('Country updated');
      } else {
        await api.post('/admin/countries', countryForm);
        toast.success('Country created');
      }
      setShowCountryModal(false);
      setEditingCountry(null);
      setCountryForm({ name: '', code: '', description: '', is_active: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save country'); }
  };

  const openEditCountry = (country) => {
    setEditingCountry(country);
    setCountryForm({ name: country.name, code: country.code, description: country.description || '', is_active: country.is_active });
    setShowCountryModal(true);
  };

  const deleteCountry = async (countryId) => {
    if (!window.confirm('Delete this country?')) return;
    try {
      await api.delete(`/admin/countries/${countryId}`);
      toast.success('Country deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete country'); }
  };

  // Regions & Cities handlers
  const loadRegionsAndCities = async (countryCode) => {
    setLoadingLocations(true);
    try {
      const [regionsRes, citiesRes] = await Promise.all([
        api.get(`/admin/regions?country_code=${encodeURIComponent(countryCode)}`),
        api.get(`/admin/cities?country_code=${encodeURIComponent(countryCode)}`),
      ]);
      setRegions(regionsRes.data.regions || []);
      const grouped = {};
      (citiesRes.data.cities || []).forEach(city => {
        if (!grouped[city.region_id]) grouped[city.region_id] = [];
        grouped[city.region_id].push(city);
      });
      setCitiesByRegion(grouped);
    } catch (e) {
      setRegions([]); setCitiesByRegion({});
    } finally {
      setLoadingLocations(false);
    }
  };

  const openManageLocations = (country) => {
    setLocationsCountry(country);
    setNewRegionName('');
    setNewCityInputs({});
    setShowLocationsModal(true);
    loadRegionsAndCities(country.code);
  };

  const addRegion = async () => {
    if (!newRegionName.trim() || !locationsCountry) return;
    try {
      await api.post('/admin/regions', { country_code: locationsCountry.code, name: newRegionName.trim() });
      setNewRegionName('');
      toast.success('Region added');
      loadRegionsAndCities(locationsCountry.code);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add region'); }
  };

  const deleteRegion = async (regionId) => {
    if (!window.confirm('Delete this region and all its cities?')) return;
    try {
      await api.delete(`/admin/regions/${regionId}`);
      toast.success('Region deleted');
      loadRegionsAndCities(locationsCountry.code);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete region'); }
  };

  const addCity = async (regionId) => {
    const name = (newCityInputs[regionId] || '').trim();
    if (!name || !locationsCountry) return;
    try {
      await api.post('/admin/cities', { region_id: regionId, country_code: locationsCountry.code, name });
      setNewCityInputs({ ...newCityInputs, [regionId]: '' });
      toast.success('City added');
      loadRegionsAndCities(locationsCountry.code);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add city'); }
  };

  const deleteCity = async (cityId) => {
    try {
      await api.delete(`/admin/cities/${cityId}`);
      toast.success('City deleted');
      loadRegionsAndCities(locationsCountry.code);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete city'); }
  };

  const autoPopulateLocations = async () => {
    if (!locationsCountry) return;
    setAutoPopulating(true);
    try {
      const isoCode = locationsCountry.code.toUpperCase();
      const { State, City } = await import('country-state-city');
      const states = State.getStatesOfCountry(isoCode);
      let regions;
      if (states.length > 0) {
        regions = states.map(state => ({
          name: state.name,
          cities: City.getCitiesOfState(isoCode, state.isoCode).map(c => c.name),
        }));
      } else {
        // Countries with no state/province subdivisions (e.g. city-states) get a single
        // region named after the country so their cities still fit the region>city hierarchy.
        const cities = City.getCitiesOfCountry(isoCode) || [];
        regions = cities.length > 0 ? [{ name: locationsCountry.name, cities: cities.map(c => c.name) }] : [];
      }
      if (regions.length === 0) {
        toast.error('No region/city data available for this country.');
        return;
      }
      const res = await api.post(`/admin/countries/${locationsCountry.code}/bulk-import-locations`, { regions });
      toast.success(res.data.message);
      loadRegionsAndCities(locationsCountry.code);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to auto-populate'); }
    finally { setAutoPopulating(false); }
  };

  // Job/Course Subcategory handlers
  const subcategoryApiBase = (type) => type === 'job' ? '/admin/job-subcategories' : '/admin/course-subcategories';

  const loadSubcategories = async (type, categoryId) => {
    setLoadingSubcategories(true);
    try {
      const res = await api.get(`${subcategoryApiBase(type)}?category_id=${categoryId}`);
      setSubcategories(res.data.subcategories || []);
    } catch (e) {
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const openManageSubcategories = (type, category) => {
    setSubcategoryType(type);
    setSubcategoryParentCategory(category);
    setNewSubcategoryName('');
    setShowSubcategoryModal(true);
    loadSubcategories(type, category.id);
  };

  const slugify = (text) => text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const addSubcategory = async () => {
    if (!newSubcategoryName.trim() || !subcategoryParentCategory) return;
    try {
      await api.post(subcategoryApiBase(subcategoryType), {
        category_id: subcategoryParentCategory.id,
        name: newSubcategoryName.trim(),
        slug: slugify(newSubcategoryName),
      });
      setNewSubcategoryName('');
      toast.success('Subcategory added');
      loadSubcategories(subcategoryType, subcategoryParentCategory.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add subcategory'); }
  };

  const deleteSubcategory = async (subcategoryId) => {
    try {
      await api.delete(`${subcategoryApiBase(subcategoryType)}/${subcategoryId}`);
      toast.success('Subcategory deleted');
      loadSubcategories(subcategoryType, subcategoryParentCategory.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete subcategory'); }
  };

  // Partner Universities handlers (Study Abroad catalogue)
  const emptyPartnerUniForm = { name: '', country: 'UK', logo_url: '', website: '', description: '', is_active: true, programs: [] };

  const openAddPartnerUni = () => {
    setEditingPartnerUni(null);
    setPartnerUniForm(emptyPartnerUniForm);
    setShowPartnerUniModal(true);
  };

  const openEditPartnerUni = (uni) => {
    setEditingPartnerUni(uni);
    setPartnerUniForm({
      name: uni.name, country: uni.country || 'UK', logo_url: uni.logo_url || '',
      website: uni.website || '', description: uni.description || '',
      is_active: uni.is_active !== false, programs: uni.programs || []
    });
    setShowPartnerUniModal(true);
  };

  const addProgramRow = () => {
    setPartnerUniForm({
      ...partnerUniForm,
      programs: [...partnerUniForm.programs, {
        name: '', degree_level: 'undergraduate', duration: '', tuition_fee: '', category: '', intake_dates: []
      }]
    });
  };

  const updateProgramRow = (index, field, value) => {
    const programs = [...partnerUniForm.programs];
    programs[index] = { ...programs[index], [field]: value };
    setPartnerUniForm({ ...partnerUniForm, programs });
  };

  const removeProgramRow = (index) => {
    setPartnerUniForm({ ...partnerUniForm, programs: partnerUniForm.programs.filter((_, i) => i !== index) });
  };

  const savePartnerUniversity = async () => {
    if (!partnerUniForm.name) {
      toast.error('University name is required');
      return;
    }
    try {
      if (editingPartnerUni) {
        await api.put(`/admin/partner-universities/${editingPartnerUni.id}`, partnerUniForm);
        toast.success('University updated');
      } else {
        await api.post('/admin/partner-universities', partnerUniForm);
        toast.success('University added');
      }
      setShowPartnerUniModal(false);
      setEditingPartnerUni(null);
      setPartnerUniForm(emptyPartnerUniForm);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save university'); }
  };

  const deletePartnerUniversity = async (universityId) => {
    if (!window.confirm('Delete this partner university and all its programs?')) return;
    try {
      await api.delete(`/admin/partner-universities/${universityId}`);
      toast.success('University deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete university'); }
  };

  // Job Categories handlers
  const saveJobCategory = async () => {
    if (!jobCategoryForm.name || !jobCategoryForm.slug) {
      toast.error('Name and slug are required');
      return;
    }
    try {
      if (editingJobCategory) {
        await api.put(`/admin/job-categories/${editingJobCategory.id}`, jobCategoryForm);
        toast.success('Job category updated');
      } else {
        await api.post('/admin/job-categories', jobCategoryForm);
        toast.success('Job category created');
      }
      setShowJobCategoryModal(false);
      setEditingJobCategory(null);
      setJobCategoryForm({ name: '', slug: '', description: '', icon: '', is_active: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save job category'); }
  };

  const openEditJobCategory = (category) => {
    setEditingJobCategory(category);
    setJobCategoryForm({ name: category.name, slug: category.slug, description: category.description || '', icon: category.icon || '', is_active: category.is_active });
    setShowJobCategoryModal(true);
  };

  const deleteJobCategory = async (categoryId) => {
    if (!window.confirm('Delete this job category?')) return;
    try {
      await api.delete(`/admin/job-categories/${categoryId}`);
      toast.success('Job category deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete job category'); }
  };

  // Course Categories handlers
  const saveCourseCategory = async () => {
    if (!courseCategoryForm.name || !courseCategoryForm.slug) {
      toast.error('Name and slug are required');
      return;
    }
    try {
      if (editingCourseCategory) {
        await api.put(`/admin/course-categories/${editingCourseCategory.id}`, courseCategoryForm);
        toast.success('Course category updated');
      } else {
        await api.post('/admin/course-categories', courseCategoryForm);
        toast.success('Course category created');
      }
      setShowCourseCategoryModal(false);
      setEditingCourseCategory(null);
      setCourseCategoryForm({ name: '', slug: '', description: '', icon: '', is_active: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save course category'); }
  };

  const openEditCourseCategory = (category) => {
    setEditingCourseCategory(category);
    setCourseCategoryForm({ name: category.name, slug: category.slug, description: category.description || '', icon: category.icon || '', is_active: category.is_active });
    setShowCourseCategoryModal(true);
  };

  const deleteCourseCategoryhandler = async (categoryId) => {
    if (!window.confirm('Delete this course category?')) return;
    try {
      await api.delete(`/admin/course-categories/${categoryId}`);
      toast.success('Course category deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete course category'); }
  };

  // Currency handlers
  const saveCurrency = async () => {
    if (!currencyForm.code || !currencyForm.name || !currencyForm.symbol) {
      toast.error('Code, name and symbol are required');
      return;
    }
    try {
      if (editingCurrency) {
        await api.put(`/admin/currencies/${editingCurrency.id}`, currencyForm);
        toast.success('Currency updated');
      } else {
        await api.post('/admin/currencies', currencyForm);
        toast.success('Currency added');
      }
      setShowCurrencyModal(false);
      setEditingCurrency(null);
      setCurrencyForm({ code: '', name: '', symbol: '', position: 'before', decimal_places: 2, is_active: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save currency'); }
  };

  const openEditCurrency = (currency) => {
    setEditingCurrency(currency);
    setCurrencyForm({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      position: currency.position || 'before',
      decimal_places: currency.decimal_places || 2,
      is_active: currency.is_active !== false
    });
    setShowCurrencyModal(true);
  };

  const deleteCurrency = async (currencyId) => {
    if (!window.confirm('Delete this currency?')) return;
    try {
      await api.delete(`/admin/currencies/${currencyId}`);
      toast.success('Currency deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete currency'); }
  };

  // User edit handler
  const openEditUserModal = (u) => {
    setEditingEditUser(u);
    setUserEditForm({ full_name: u.full_name || '', email: u.email || '', phone: u.phone || '', location: u.location || '', bio: u.bio || '' });
    setShowUserEditModal(true);
  };

  const saveUserEdit = async () => {
    if (!editingEditUser) return;
    try {
      await api.put(`/admin/users/${editingEditUser.id}/edit`, userEditForm);
      toast.success('User updated successfully');
      setShowUserEditModal(false);
      setEditingEditUser(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update user'); }
  };

  const updateRecruitmentStatus = async (reqId, status) => {
    try {
      await api.put(`/admin/recruitment-requests/${reqId}/status?status=${status}`);
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  const updateStudentAppStatus = async (appId, status) => {
    try {
      await api.put(`/admin/student-applications/${appId}/status?status=${status}`);
      toast.success('Application status updated');
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      await api.delete(`/admin/subscription-plans/${planId}`);
      toast.success('Plan deleted');
      fetchData();
    } catch { toast.error('Failed to delete plan'); }
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name, slug: plan.slug, price: plan.price, description: plan.description || '',
      features: (plan.features || []).join('\n'), billing_cycle: plan.billing_cycle || 'monthly',
      role: plan.role || 'employer', limits: plan.limits || {},
      is_active: plan.is_active !== false, sort_order: plan.sort_order || 0
    });
    setShowPlanModal(true);
  };

  // Blog handlers
  const saveBlogPost = async () => {
    try {
      const payload = { ...blogForm, tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (editingPost) {
        await api.put(`/admin/blog/posts/${editingPost.id}`, payload);
        toast.success('Post updated');
      } else {
        await api.post('/admin/blog/posts', payload);
        toast.success('Post created');
      }
      setShowBlogModal(false); setEditingPost(null); fetchData();
    } catch { toast.error('Failed to save post'); }
  };
  const deleteBlogPost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try { await api.delete(`/admin/blog/posts/${postId}`); toast.success('Post deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };
  const approveComment = async (commentId) => {
    try { await api.put(`/admin/blog/comments/${commentId}/approve`); toast.success('Comment approved'); fetchData(); }
    catch { toast.error('Failed to approve'); }
  };
  const deleteComment = async (commentId) => {
    try { await api.delete(`/admin/blog/comments/${commentId}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };
  // Privacy policy handlers
  const savePrivacy = async () => {
    setSavingPrivacy(true);
    try { await api.put('/admin/pages/privacy', privacyForm); toast.success('Privacy policy saved'); }
    catch { toast.error('Failed to save privacy policy'); }
    finally { setSavingPrivacy(false); }
  };
  const updatePrivacySection = (i, patch) =>
    setPrivacyForm(f => ({ ...f, sections: f.sections.map((sec, idx) => idx === i ? { ...sec, ...patch } : sec) }));
  const movePrivacySection = (i, dir) =>
    setPrivacyForm(f => {
      const arr = [...f.sections]; const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, sections: arr };
    });

  // Branding handler
  const saveBranding = async () => {
    setSavingBranding(true);
    try { await api.put('/admin/branding', brandingForm); toast.success('Branding saved'); fetchData(); }
    catch { toast.error('Failed to save branding'); }
    finally { setSavingBranding(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Do NOT set Content-Type manually — axios must auto-set multipart/form-data with the boundary
      const res = await api.post('/admin/branding/logo', formData);
      setBrandingForm(f => ({ ...f, logo_url: res.data.logo_url }));
      // Refresh the global branding context so the Navbar logo updates immediately
      refreshBranding();
      toast.success('Logo uploaded');
    } catch { toast.error('Logo upload failed'); }
    finally { setUploadingLogo(false); }
  };

  // Skills Tests handlers
  const saveTest = async () => {
    try {
      const payload = { ...testForm, duration_minutes: parseInt(testForm.duration_minutes), passing_score: parseInt(testForm.passing_score) };
      if (editingTest) {
        await api.put(`/admin/skills-tests/${editingTest.id}`, payload);
        toast.success('Test updated');
      } else {
        await api.post('/admin/skills-tests', payload);
        toast.success('Test created');
      }
      setShowTestModal(false); setEditingTest(null); fetchData();
    } catch { toast.error('Failed to save test'); }
  };
  const deleteTest = async (testId) => {
    if (!window.confirm('Delete this test?')) return;
    try { await api.delete(`/admin/skills-tests/${testId}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };
  const addQuestion = () => {
    setTestForm(f => ({ ...f, questions: [...f.questions, { ...questionForm, id: crypto.randomUUID() }] }));
    setQuestionForm({ question: '', options: ['','','',''], correct_answer: 0, explanation: '' });
  };
  const removeQuestion = (idx) => setTestForm(f => ({ ...f, questions: f.questions.filter((_,i) => i !== idx) }));
  // CMS handlers
  const saveCmsSection = async (section) => {
    setSavingCms(true);
    try {
      await api.put(`/admin/content/${section}`, cmsContent[section] || {});
      toast.success('Content saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingCms(false); }
  };
  // Email template handlers
  const saveTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      await api.put(`/admin/email-templates/${editingTemplate.name}`, templateForm);
      toast.success('Template saved');
      setEditingTemplate(null); fetchData();
    } catch { toast.error('Failed to save template'); }
    finally { setSavingTemplate(false); }
  };
  const resetTemplate = async (name) => {
    if (!window.confirm('Reset to default template?')) return;
    try { await api.delete(`/admin/email-templates/${name}`); toast.success('Reset to default'); fetchData(); }
    catch { toast.error('Failed to reset'); }
  };

  // Security / CAPTCHA handlers
  const saveCaptchaSettings = async () => {
    setSavingCaptcha(true);
    try {
      await api.put('/admin/settings/captcha', captchaSettings);
      toast.success('CAPTCHA settings saved');
    } catch { toast.error('Failed to save CAPTCHA settings'); }
    finally { setSavingCaptcha(false); }
  };

  // Email handlers
  const sendDigest = async (type) => {
    setSendingDigest(type);
    try {
      const endpoints = { instant: '/admin/notifications/send-job-matches', daily: '/admin/notifications/send-daily-digest', weekly: '/admin/notifications/send-weekly-digest' };
      await api.post(endpoints[type]);
      toast.success(`${type.charAt(0).toUpperCase()+type.slice(1)} notifications queued`);
    } catch { toast.error('Failed to send notifications'); }
    finally { setSendingDigest(''); }
  };

  const saveSmtpSettings = async () => {
    if (!smtpForm.smtp_host.trim() || !smtpForm.smtp_user.trim()) {
      toast.error('SMTP host and username are required');
      return;
    }
    setSavingSmtp(true);
    try {
      await api.put('/admin/settings/smtp', smtpForm);
      toast.success('SMTP settings saved — emails now send using these settings');
      setSmtpForm(f => ({ ...f, smtp_pass: '' }));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const clearSmtpSettings = async () => {
    setClearingSmtp(true);
    try {
      await api.delete('/admin/settings/smtp');
      toast.success('SMTP settings cleared — falling back to environment variables');
      fetchData();
    } catch { toast.error('Failed to clear SMTP settings'); }
    finally { setClearingSmtp(false); }
  };

  const testSmtpConnection = async () => {
    setTestingSmtp(true);
    setSmtpTestResult(null);
    try {
      const res = await api.post('/admin/notifications/test-smtp');
      setSmtpTestResult(res.data);
      if (res.data.status === 'ok') {
        toast.success('SMTP connection successful');
      } else {
        toast.error(`SMTP connection failed: ${res.data.message}`);
      }
    } catch (err) {
      toast.error('Failed to test SMTP connection');
    } finally {
      setTestingSmtp(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailForm.to_email) {
      toast.error('Email address required');
      return;
    }
    setSendingTestEmail(true);
    try {
      const res = await api.post('/admin/notifications/send-test-email', testEmailForm);
      if (res.data.status === 'sent') {
        toast.success(`Test email sent to ${testEmailForm.to_email}`);
        setShowTestEmailModal(false);
        setTestEmailForm({ to_email: '', template: 'welcome' });
      } else {
        toast.error('Failed to send test email');
      }
    } catch { toast.error('Failed to send test email'); }
    finally { setSendingTestEmail(false); }
  };

  const getUserTypeColor = (type) => {
    const c = { admin: 'bg-red-100 text-red-700', employer: 'bg-blue-100 text-blue-700', job_seeker: 'bg-emerald-100 text-emerald-700', course_provider: 'bg-purple-100 text-purple-700', student: 'bg-amber-100 text-amber-700' };
    return c[type] || 'bg-gray-100 text-gray-700';
  };

  const getPaymentStatusColor = (status) => {
    const c = { paid: 'bg-emerald-100 text-emerald-700', pending_bank_transfer: 'bg-blue-100 text-blue-700', initiated: 'bg-amber-100 text-amber-700' };
    return c[status] || 'bg-gray-100 text-gray-700';
  };

  const statCards = stats ? [
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Active Jobs', value: stats.active_jobs, icon: Briefcase, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { label: 'Jobs Last 24h', value: stats.jobs_last_24h, icon: Briefcase, color: 'text-orange-600', bg: 'bg-orange-100' },
    { label: 'Total Courses', value: stats.total_courses, icon: GraduationCap, color: 'text-purple-600', bg: 'bg-purple-100' },
    { label: 'Applications', value: stats.total_applications, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-100' },
    { label: 'Apps Last 24h', value: stats.applications_last_24h, icon: TrendingUp, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    { label: 'Employers', value: stats.total_employers, icon: Building2, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Enrollments', value: stats.total_enrollments, icon: GraduationCap, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { label: 'Students', value: stats.total_students, icon: Globe, color: 'text-amber-600', bg: 'bg-amber-100' },
    { label: 'New Candidates 24h', value: stats.new_candidates_last_24h, icon: Users, color: 'text-cyan-600', bg: 'bg-cyan-100' },
  ] : [];

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-[#003366] min-h-screen sticky top-0 hidden lg:block">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold">Admin Panel</p>
                <p className="text-white/60 text-xs">WorksRun</p>
              </div>
            </div>
            <nav className="space-y-1">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                  data-testid={`admin-tab-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-white/60 hover:text-white hover:bg-white/5">
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </nav>
          </div>
        </aside>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-64 p-0 bg-[#003366] border-0">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <p className="text-white font-bold text-sm">Admin Panel</p>
              </div>
              <nav className="space-y-1">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-white/60 hover:text-white hover:bg-white/5">
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 pb-8">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-6 pb-4 border-b">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">
              {TABS.find(t => t.id === activeTab)?.label}
            </h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {TABS.find(t => t.id === activeTab)?.label}
            </h1>
            <p className="text-muted-foreground mb-8">{TAB_DESCRIPTIONS[activeTab] || 'Manage your platform'}</p>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {statCards.map((s, i) => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">{s.label}</p>
                            <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                          </div>
                          <div className={`w-12 h-12 rounded-xl ${s.bg} flex items-center justify-center`}>
                            <s.icon className={`w-6 h-6 ${s.color}`} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {[{ label: 'Manage Users', tab: 'users', icon: Users },
                        { label: 'Review Jobs', tab: 'jobs', icon: Briefcase },
                        { label: 'Recruitment Requests', tab: 'recruitment', icon: Building2 },
                        { label: 'Student Services', tab: 'student_services', icon: Globe },
                        { label: 'Revenue Analytics', tab: 'revenue', icon: PoundSterling },
                        { label: 'Manage Plans', tab: 'subscriptions', icon: Settings }
                      ].map(a => (
                        <Button key={a.tab} variant="outline" className="w-full justify-between"
                          onClick={() => setActiveTab(a.tab)} data-testid={`quick-${a.tab}`}>
                          <span className="flex items-center gap-2"><a.icon className="w-4 h-4" />{a.label}</span>
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      ))}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle>Platform Health</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                      {[{ label: 'Total Employers', value: stats?.total_employers || 0 },
                        { label: 'Active Jobs', value: stats?.active_jobs || 0 },
                        { label: 'Total Enrollments', value: stats?.total_enrollments || 0 },
                        { label: 'Total Students', value: stats?.total_students || 0 },
                      ].map((m, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="text-sm text-muted-foreground">{m.label}</span>
                          <span className="font-bold">{m.value}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex gap-3 flex-wrap">
                  {['all', 'job_seeker', 'employer', 'admin', 'course_provider'].map(f => (
                    <Button key={f} size="sm" variant={userFilter === f ? 'default' : 'outline'}
                      onClick={() => setUserFilter(f)} className={userFilter === f ? 'bg-[#003366]' : ''}>
                      {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Button>
                  ))}
                </div>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{usersTotal} Users</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? <Skeleton className="h-40" /> : (
                      <div className="space-y-3">
                        {users.map(u => (
                          <div key={u.id} className="flex items-center justify-between p-4 rounded-lg border" data-testid={`user-row-${u.id}`}>
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-[#003366] flex items-center justify-center text-white font-medium">
                                {u.full_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium">{u.full_name}</p>
                                <p className="text-sm text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={getUserTypeColor(u.user_type)}>{u.user_type}</Badge>
                              {u.is_verified && <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditUserModal(u)}><Edit className="w-4 h-4 mr-2" />Edit User</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setAccessUser(u)}><Globe className="w-4 h-4 mr-2" />Country access{['university_manager', 'course_provider', 'event_host'].includes(u.user_type) ? ' & commission' : ''}</DropdownMenuItem>
                                  {!u.is_verified && <DropdownMenuItem onClick={() => verifyUser(u.id)}><UserCheck className="w-4 h-4 mr-2" />Verify User</DropdownMenuItem>}
                                  <DropdownMenuItem onClick={() => toggleUser(u.id, !u.is_active)}>
                                    {u.is_active ? <><Ban className="w-4 h-4 mr-2" />Deactivate</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Activate</>}
                                  </DropdownMenuItem>
                                  {u.user_type !== 'admin' && (
                                    <DropdownMenuItem className="text-red-600" onClick={() => deleteUser(u.id)}>
                                      <Trash2 className="w-4 h-4 mr-2" />Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Countries Tab */}
            {activeTab === 'countries' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Countries</h2>
                  <Button onClick={() => {
                    setEditingCountry(null);
                    setCountryForm({ name: '', code: '', description: '', is_active: true });
                    setShowCountryModal(true);
                  }} className="bg-[#003366] hover:bg-[#003366]/90"><Plus className="w-4 h-4 mr-2" />Add Country</Button>
                </div>
                <Card>
                  <CardContent>
                    {loading ? <Skeleton className="h-40" /> : (
                      <div className="space-y-3">
                        {countries.length === 0 ? (
                          <p className="text-center text-muted-foreground py-8">No countries yet</p>
                        ) : (
                          countries.map(c => (
                            <div key={c.id} className="flex items-center justify-between p-4 rounded-lg border">
                              <div>
                                <p className="font-medium">{c.name}</p>
                                <p className="text-sm text-muted-foreground">{c.code} {c.description && `- ${c.description}`}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className={c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                                  {c.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                <Button size="sm" variant="outline" onClick={() => openManageLocations(c)}>
                                  <MapPin className="w-4 h-4 mr-1" />Regions & Cities
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openEditCountry(c)}>Edit</Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteCountry(c.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Job Categories Tab */}
            {activeTab === 'job_categories' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Job Categories</h2>
                  <Button onClick={() => {
                    setEditingJobCategory(null);
                    setJobCategoryForm({ name: '', slug: '', description: '', icon: '', is_active: true });
                    setShowJobCategoryModal(true);
                  }} className="bg-[#003366] hover:bg-[#003366]/90"><Plus className="w-4 h-4 mr-2" />Add Category</Button>
                </div>
                <Card>
                  <CardContent>
                    {loading ? <Skeleton className="h-40" /> : (
                      <div className="space-y-3">
                        {jobCategories.length === 0 ? (
                          <p className="text-center text-muted-foreground py-8">No job categories yet</p>
                        ) : (
                          jobCategories.map(c => (
                            <div key={c.id} className="flex items-center justify-between p-4 rounded-lg border">
                              <div>
                                <p className="font-medium">{c.name}</p>
                                <p className="text-sm text-muted-foreground">{c.slug} {c.description && `- ${c.description}`}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className={c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                                  {c.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                <Button size="sm" variant="outline" onClick={() => openManageSubcategories('job', c)}>Subcategories</Button>
                                <Button size="sm" variant="outline" onClick={() => openEditJobCategory(c)}>Edit</Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteJobCategory(c.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Course Categories Tab */}
            {activeTab === 'course_categories' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Course Categories</h2>
                  <Button onClick={() => {
                    setEditingCourseCategory(null);
                    setCourseCategoryForm({ name: '', slug: '', description: '', icon: '', is_active: true });
                    setShowCourseCategoryModal(true);
                  }} className="bg-[#003366] hover:bg-[#003366]/90"><Plus className="w-4 h-4 mr-2" />Add Category</Button>
                </div>
                <Card>
                  <CardContent>
                    {loading ? <Skeleton className="h-40" /> : (
                      <div className="space-y-3">
                        {courseCategories.length === 0 ? (
                          <p className="text-center text-muted-foreground py-8">No course categories yet</p>
                        ) : (
                          courseCategories.map(c => (
                            <div key={c.id} className="flex items-center justify-between p-4 rounded-lg border">
                              <div>
                                <p className="font-medium">{c.name}</p>
                                <p className="text-sm text-muted-foreground">{c.slug} {c.description && `- ${c.description}`}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge className={c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                                  {c.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                <Button size="sm" variant="outline" onClick={() => openManageSubcategories('course', c)}>Subcategories</Button>
                                <Button size="sm" variant="outline" onClick={() => openEditCourseCategory(c)}>Edit</Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteCourseCategoryhandler(c.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Currencies Tab */}
            {activeTab === 'currencies' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Currencies</h2>
                  <Button onClick={() => {
                    setEditingCurrency(null);
                    setCurrencyForm({ code: '', name: '', symbol: '', position: 'before', decimal_places: 2, is_active: true });
                    setShowCurrencyModal(true);
                  }} className="bg-[#003366] hover:bg-[#003366]/90"><Plus className="w-4 h-4 mr-2" />Add Currency</Button>
                </div>
                <Card>
                  <CardContent>
                    {loading ? <Skeleton className="h-40" /> : (
                      <div className="space-y-3">
                        {currencies.length === 0 ? (
                          <p className="text-center text-muted-foreground py-8">No currencies configured yet</p>
                        ) : (
                          currencies.map(curr => (
                            <div key={curr.id} className="flex items-center justify-between p-4 rounded-lg border">
                              <div>
                                <p className="font-medium">{curr.code} - {curr.name}</p>
                                <p className="text-sm text-muted-foreground">Symbol: {curr.symbol} ({curr.position}) • Decimals: {curr.decimal_places}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                {curr.is_default && <Badge className="bg-blue-100 text-blue-700">Default</Badge>}
                                <Badge className={curr.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                                  {curr.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                <Button size="sm" variant="outline" onClick={() => openEditCurrency(curr)}>Edit</Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteCurrency(curr.id)} disabled={curr.is_default}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Jobs Tab */}
            {activeTab === 'jobs' && (
              <Card>
                <CardHeader><CardTitle>{jobsTotal} Jobs</CardTitle></CardHeader>
                <CardContent>
                  {loading ? <Skeleton className="h-40" /> : (
                    <div className="space-y-3">
                      {jobs.map(j => (
                        <div key={j.id} className="flex items-center justify-between p-4 rounded-lg border" data-testid={`job-row-${j.id}`}>
                          <div>
                            <p className="font-medium">{j.title}</p>
                            <p className="text-sm text-muted-foreground">{j.company_name} - {j.location}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={j.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>{j.status}</Badge>
                            <span className="text-sm text-muted-foreground">{j.applications_count || 0} apps</span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {j.status !== 'active' && <DropdownMenuItem onClick={() => updateJobStatus(j.id, 'active')}><CheckCircle2 className="w-4 h-4 mr-2" />Activate</DropdownMenuItem>}
                                {j.status === 'active' && <DropdownMenuItem onClick={() => updateJobStatus(j.id, 'paused')}><Ban className="w-4 h-4 mr-2" />Pause</DropdownMenuItem>}
                                <DropdownMenuItem className="text-red-600" onClick={() => deleteJob(j.id)}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Applications Tab */}
            {activeTab === 'applications' && (
              <Card>
                <CardHeader><CardTitle>{applicationsTotal} Job Applications</CardTitle></CardHeader>
                <CardContent>
                  {loading ? <Skeleton className="h-40" /> : (
                    <div className="space-y-2">
                      {applications.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No applications yet</p>
                      ) : (
                        applications.map(app => (
                          <div key={app.id} onClick={() => { setSelectedApplication(app); setShowApplicationModal(true); }} className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 cursor-pointer transition">
                            <div className="flex-1">
                              <p className="font-medium">{app.job?.title || 'Job Title'}</p>
                              <p className="text-sm text-muted-foreground">{app.applicant?.name || 'Applicant'} • {app.applicant?.email || 'N/A'}</p>
                              <p className="text-xs text-muted-foreground">{app.job?.employer_name || 'Company'} • {app.created_at?.slice(0, 10)}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={{
                                pending: 'bg-amber-100 text-amber-700',
                                reviewing: 'bg-blue-100 text-blue-700',
                                shortlisted: 'bg-purple-100 text-purple-700',
                                hired: 'bg-emerald-100 text-emerald-700',
                                rejected: 'bg-red-100 text-red-700',
                              }[app.status] || 'bg-gray-100 text-gray-700'}>
                                {app.status?.replace('_', ' ')}
                              </Badge>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Application Detail Modal */}
            {showApplicationModal && selectedApplication && (
              <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Job Application Details</DialogTitle>
                    <DialogDescription>Complete application information and history</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Job Position</p>
                        <p className="font-semibold text-lg">{selectedApplication.job?.title}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Employer</p>
                        <p className="font-semibold">{selectedApplication.job?.employer_name}</p>
                      </div>
                    </div>

                    {/* Applicant Info */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Applicant Information</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Name</p>
                          <p className="font-medium">{selectedApplication.applicant?.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium text-blue-600">{selectedApplication.applicant?.email}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Status</p>
                          <Badge className={{
                            pending: 'bg-amber-100 text-amber-700',
                            reviewing: 'bg-blue-100 text-blue-700',
                            shortlisted: 'bg-purple-100 text-purple-700',
                            hired: 'bg-emerald-100 text-emerald-700',
                            rejected: 'bg-red-100 text-red-700',
                          }[selectedApplication.status] || 'bg-gray-100 text-gray-700'}>
                            {selectedApplication.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Applied</p>
                          <p className="font-medium">{new Date(selectedApplication.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Cover Letter */}
                    {selectedApplication.cover_letter && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-2">Cover Letter</h3>
                        <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedApplication.cover_letter}</p>
                      </div>
                    )}

                    {/* Application Notes */}
                    {selectedApplication.notes && selectedApplication.notes.length > 0 && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-4">Notes</h3>
                        <div className="space-y-3">
                          {selectedApplication.notes.map((note, idx) => (
                            <div key={idx} className="bg-blue-50 p-3 rounded text-sm">
                              <p className="font-medium text-blue-900">{note.added_by || 'Admin'}</p>
                              <p className="text-blue-700 mt-1">{note.note}</p>
                              <p className="text-xs text-blue-600 mt-2">{note.added_at ? new Date(note.added_at).toLocaleDateString() : ''}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowApplicationModal(false)}>Close</Button>
                      <Button onClick={() => { navigator.clipboard.writeText(selectedApplication.applicant?.email || 'N/A'); toast.success('Email copied'); }} className="flex-1">
                        Copy Email
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <Card>
                <CardHeader><CardTitle>{coursesTotal} Courses</CardTitle></CardHeader>
                <CardContent>
                  {loading ? <Skeleton className="h-40" /> : (
                    <div className="space-y-3">
                      {courses.map(c => (
                        <div key={c.id} onClick={() => { setSelectedCourse(c); setShowCourseModal(true); }} className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 cursor-pointer transition">
                          <div className="flex-1">
                            <p className="font-medium">{c.title}</p>
                            <p className="text-sm text-muted-foreground">{c.institution_name} - {c.is_free ? 'Free' : formatMoney(c.price, c.currency)}</p>
                            {c.duration && <p className="text-xs text-muted-foreground">Duration: {c.duration}</p>}
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>{c.status}</Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {c.status !== 'active' && <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updateCourseStatus(c.id, 'active'); }}>Activate</DropdownMenuItem>}
                                {c.status === 'active' && <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updateCourseStatus(c.id, 'archived'); }}>Archive</DropdownMenuItem>}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-xl font-semibold">Events ({adminEvents.length})</h2>
                    <p className="text-sm text-muted-foreground mt-1">Create and manage events visible to users on the platform.</p>
                  </div>
                  <Button onClick={openNewEvent} className="bg-[#003366] hover:bg-[#003366]/90">
                    <Plus className="w-4 h-4 mr-2" />New Event
                  </Button>
                </div>

                {loading && <Skeleton className="h-48" />}

                {!loading && adminEvents.length === 0 && (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold text-lg mb-1">No events yet</h3>
                      <p className="text-muted-foreground mb-4">Create your first event to show it on the platform.</p>
                      <Button onClick={openNewEvent} className="bg-[#003366] hover:bg-[#003366]/90">
                        <Plus className="w-4 h-4 mr-2" />Create Event
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {!loading && adminEvents.length > 0 && (
                  <div className="space-y-3">
                    {adminEvents.map(ev => (
                      <div key={ev.id} className="flex items-start justify-between p-4 rounded-xl border bg-white gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold truncate">{ev.title}</p>
                            <Badge className={
                              ev.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                              ev.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              ev.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                              'bg-amber-100 text-amber-700'
                            }>{ev.status || 'active'}</Badge>
                            {ev.is_virtual && <Badge className="bg-blue-100 text-blue-700">Virtual</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                            {ev.date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(ev.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                            {ev.location && <span>📍 {ev.location}</span>}
                            {ev.organizer_name && <span>👤 {ev.organizer_name}</span>}
                            {ev.attendees_count > 0 && <span>🎫 {ev.attendees_count} attendees</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditEvent(ev)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                              {ev.status !== 'active' && <DropdownMenuItem onClick={() => setEventStatus(ev.id, 'active')}>Set Active</DropdownMenuItem>}
                              {ev.status !== 'cancelled' && <DropdownMenuItem onClick={() => setEventStatus(ev.id, 'cancelled')}>Cancel Event</DropdownMenuItem>}
                              {ev.status !== 'completed' && <DropdownMenuItem onClick={() => setEventStatus(ev.id, 'completed')}>Mark Completed</DropdownMenuItem>}
                              <DropdownMenuItem onClick={() => deleteEvent(ev.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Universities Tab */}
            {activeTab === 'universities' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">Universities ({adminUniversities.length})</h2>
                  <p className="text-sm text-muted-foreground mt-1">Review registered profiles and manage partner universities and their programs.</p>
                </div>

                {loading && <Skeleton className="h-48" />}

                {!loading && adminUniversities.length === 0 && (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold text-lg mb-1">No university profiles yet</h3>
                      <p className="text-muted-foreground">Universities will appear here once they register on the platform.</p>
                    </CardContent>
                  </Card>
                )}

                {!loading && adminUniversities.length > 0 && (
                  <div className="space-y-3">
                    {adminUniversities.map(u => (
                      <div key={u.id} onClick={() => { setSelectedUniversity(u); setUniversityReviewNotes(u.review_notes || ''); setShowUniversityModal(true); }} className={`flex items-start justify-between p-4 rounded-xl border bg-white gap-4 hover:shadow-md transition cursor-pointer ${!u.is_active ? 'opacity-60' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold truncate">{u.institution_name || u.name || 'Unnamed University'}</p>
                            {u.is_verified ? (
                              <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1 inline" />Verified</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700">Pending Verification</Badge>
                            )}
                            {!u.is_active && <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                            {u.country && <span>🌍 {u.country}</span>}
                            {u.website && <a href={u.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline truncate max-w-xs">{u.website}</a>}
                            {u.created_at && <span>Joined {new Date(u.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {!u.is_verified && (
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); verifyUniversity(u.id); }} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                              <ShieldCheck className="w-3.5 h-3.5 mr-1" />Verify
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); toggleUniversity(u.id, u.is_active); }}>
                            {u.is_active ? <Ban className="w-4 h-4 text-amber-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteUniversity(u.id); }} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t pt-6 space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold">Partner Universities ({partnerUniversities.length})</h3>
                      <p className="text-sm text-muted-foreground mt-1">Add universities and programs shown on the International Students page.</p>
                    </div>
                    <Button onClick={openAddPartnerUni} className="bg-[#003366] hover:bg-[#003366]/90 shrink-0">
                      <Plus className="w-4 h-4 mr-2" />Add Partner University
                    </Button>
                  </div>
                  {partnerUniversities.length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center">
                        <GraduationCap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No partner universities yet. Add one with its available programs.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {partnerUniversities.map(uni => (
                            <div key={uni.id} className="flex items-center justify-between p-4 gap-4">
                              <div className="min-w-0">
                                <p className="font-medium truncate">{uni.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {uni.country} • {(uni.programs || []).length} program{(uni.programs || []).length === 1 ? '' : 's'}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <Badge className={uni.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                                  {uni.is_active !== false ? 'Active' : 'Inactive'}
                                </Badge>
                                <Button size="sm" variant="ghost" onClick={() => openEditPartnerUni(uni)} aria-label={`Edit ${uni.name}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deletePartnerUniversity(uni.id)} aria-label={`Delete ${uni.name}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {/* Course Providers Tab — deliberately separate from Universities */}
            {activeTab === 'course_providers' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">Course Providers ({adminCourseProviders.length})</h2>
                  <p className="text-sm text-muted-foreground mt-1">Review, verify and manage course provider/trainer profiles registered on the platform.</p>
                </div>

                {loading && <Skeleton className="h-48" />}

                {!loading && adminCourseProviders.length === 0 && (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold text-lg mb-1">No course provider profiles yet</h3>
                      <p className="text-muted-foreground">Course providers will appear here once they register on the platform.</p>
                    </CardContent>
                  </Card>
                )}

                {!loading && adminCourseProviders.length > 0 && (
                  <div className="space-y-3">
                    {adminCourseProviders.map(p => (
                      <div key={p.id} onClick={() => { setSelectedCourseProvider(p); setCourseProviderReviewNotes(p.review_notes || ''); setShowCourseProviderModal(true); }} className={`flex items-start justify-between p-4 rounded-xl border bg-white gap-4 hover:shadow-md transition cursor-pointer ${!p.is_active ? 'opacity-60' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold truncate">{p.business_name || 'Unnamed Provider'}</p>
                            {p.is_verified ? (
                              <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1 inline" />Verified</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700">Pending Verification</Badge>
                            )}
                            {!p.is_active && <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                            {p.category && <span className="capitalize">{p.category.replace(/_/g, ' ')}</span>}
                            {p.location && <span>📍 {p.location}</span>}
                            {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline truncate max-w-xs">{p.website}</a>}
                            {p.created_at && <span>Joined {new Date(p.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {!p.is_verified && (
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); verifyCourseProvider(p.id); }} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                              <ShieldCheck className="w-3.5 h-3.5 mr-1" />Verify
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); toggleCourseProvider(p.id, p.is_active); }}>
                            {p.is_active ? <Ban className="w-4 h-4 text-amber-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteCourseProvider(p.id); }} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Course Provider Detail Modal */}
            {showCourseProviderModal && selectedCourseProvider && (
              <Dialog open={showCourseProviderModal} onOpenChange={setShowCourseProviderModal}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Course Provider Review</DialogTitle>
                    <DialogDescription>Verify and manage course provider/trainer registration</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Business/Trainer Name</p>
                        <p className="font-semibold text-lg">{selectedCourseProvider.business_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge className={selectedCourseProvider.is_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {selectedCourseProvider.is_verified ? 'Verified' : 'Pending Verification'}
                        </Badge>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Contact Information</h3>
                      <div className="grid grid-cols-2 gap-6">
                        {selectedCourseProvider.country && <div>
                          <p className="text-sm text-muted-foreground">Country</p>
                          <p className="font-medium">{selectedCourseProvider.country}</p>
                        </div>}
                        {selectedCourseProvider.email && <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium text-blue-600">{selectedCourseProvider.email}</p>
                        </div>}
                        {selectedCourseProvider.phone && <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <p className="font-medium">{selectedCourseProvider.phone}</p>
                        </div>}
                        {selectedCourseProvider.website && <div>
                          <p className="text-sm text-muted-foreground">Website</p>
                          <a href={selectedCourseProvider.website} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline truncate">{selectedCourseProvider.website}</a>
                        </div>}
                      </div>
                    </div>

                    {selectedCourseProvider.about && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-2">About</h3>
                        <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedCourseProvider.about}</p>
                      </div>
                    )}

                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Provider Details</h3>
                      <div className="grid grid-cols-2 gap-6 text-sm">
                        {selectedCourseProvider.category && <div>
                          <p className="text-muted-foreground">Category</p>
                          <p className="font-medium capitalize">{selectedCourseProvider.category.replace(/_/g, ' ')}</p>
                        </div>}
                        {(selectedCourseProvider.expertise || []).length > 0 && <div>
                          <p className="text-muted-foreground">Expertise</p>
                          <p className="font-medium">{selectedCourseProvider.expertise.join(', ')}</p>
                        </div>}
                        <div>
                          <p className="text-muted-foreground">Registration Date</p>
                          <p className="font-medium">{new Date(selectedCourseProvider.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-2">Review Notes</h3>
                      <textarea
                        value={courseProviderReviewNotes}
                        onChange={(e) => setCourseProviderReviewNotes(e.target.value)}
                        placeholder="Add verification notes or comments here..."
                        className="w-full border rounded-md p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowCourseProviderModal(false)}>Close</Button>
                      {!selectedCourseProvider.is_verified && (
                        <Button className="bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => {
                          reviewCourseProvider(selectedCourseProvider.id, 'approve');
                          setShowCourseProviderModal(false);
                        }}>
                          <ShieldCheck className="w-4 h-4 mr-2" />Approve & Verify
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => {
                        reviewCourseProvider(selectedCourseProvider.id, 'reject');
                        setShowCourseProviderModal(false);
                      }}>Reject</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Course Detail Modal */}
            {showCourseModal && selectedCourse && (
              <Dialog open={showCourseModal} onOpenChange={setShowCourseModal}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Course Details</DialogTitle>
                    <DialogDescription>Complete course information</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Course Title</p>
                        <p className="font-semibold text-lg">{selectedCourse.title}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Institution</p>
                        <p className="font-semibold">{selectedCourse.institution_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Price</p>
                        <p className="font-semibold">{selectedCourse.is_free ? 'Free' : formatMoney(selectedCourse.price, selectedCourse.currency)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge className={selectedCourse.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>{selectedCourse.status}</Badge>
                      </div>
                    </div>

                    {selectedCourse.description && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-2">Description</h3>
                        <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedCourse.description}</p>
                      </div>
                    )}

                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Course Information</h3>
                      <div className="grid grid-cols-2 gap-6 text-sm">
                        {selectedCourse.duration && <div>
                          <p className="text-muted-foreground">Duration</p>
                          <p className="font-medium">{selectedCourse.duration}</p>
                        </div>}
                        {selectedCourse.level && <div>
                          <p className="text-muted-foreground">Level</p>
                          <p className="font-medium capitalize">{selectedCourse.level}</p>
                        </div>}
                        {selectedCourse.category && <div>
                          <p className="text-muted-foreground">Category</p>
                          <p className="font-medium">{selectedCourse.category}</p>
                        </div>}
                        {selectedCourse.language && <div>
                          <p className="text-muted-foreground">Language</p>
                          <p className="font-medium">{selectedCourse.language}</p>
                        </div>}
                      </div>
                    </div>

                    {selectedCourse.prerequisites && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-2">Prerequisites</h3>
                        <p className="text-sm">{selectedCourse.prerequisites}</p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowCourseModal(false)}>Close</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* University Detail Modal */}
            {showUniversityModal && selectedUniversity && (
              <Dialog open={showUniversityModal} onOpenChange={setShowUniversityModal}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>University Profile Review</DialogTitle>
                    <DialogDescription>Verify and manage university registration</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Institution Name</p>
                        <p className="font-semibold text-lg">{selectedUniversity.institution_name || selectedUniversity.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge className={selectedUniversity.is_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {selectedUniversity.is_verified ? 'Verified' : 'Pending Verification'}
                        </Badge>
                      </div>
                    </div>

                    {/* Contact & Location */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Contact Information</h3>
                      <div className="grid grid-cols-2 gap-6">
                        {selectedUniversity.country && <div>
                          <p className="text-sm text-muted-foreground">Country</p>
                          <p className="font-medium">{selectedUniversity.country}</p>
                        </div>}
                        {selectedUniversity.email && <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium text-blue-600">{selectedUniversity.email}</p>
                        </div>}
                        {selectedUniversity.phone && <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <p className="font-medium">{selectedUniversity.phone}</p>
                        </div>}
                        {selectedUniversity.website && <div>
                          <p className="text-sm text-muted-foreground">Website</p>
                          <a href={selectedUniversity.website} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline truncate">{selectedUniversity.website}</a>
                        </div>}
                      </div>
                    </div>

                    {/* About */}
                    {selectedUniversity.about && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-2">About</h3>
                        <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedUniversity.about}</p>
                      </div>
                    )}

                    {/* Details */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">University Details</h3>
                      <div className="grid grid-cols-2 gap-6 text-sm">
                        {selectedUniversity.university_type && <div>
                          <p className="text-muted-foreground">Type</p>
                          <p className="font-medium capitalize">{selectedUniversity.university_type}</p>
                        </div>}
                        {selectedUniversity.founded_year && <div>
                          <p className="text-muted-foreground">Founded</p>
                          <p className="font-medium">{selectedUniversity.founded_year}</p>
                        </div>}
                        {selectedUniversity.accreditation && <div>
                          <p className="text-muted-foreground">Accreditation</p>
                          <p className="font-medium">{selectedUniversity.accreditation}</p>
                        </div>}
                        <div>
                          <p className="text-muted-foreground">Registration Date</p>
                          <p className="font-medium">{new Date(selectedUniversity.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Review Notes */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-2">Review Notes</h3>
                      <textarea
                        value={universityReviewNotes}
                        onChange={(e) => setUniversityReviewNotes(e.target.value)}
                        placeholder="Add verification notes or comments here..."
                        className="w-full border rounded-md p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowUniversityModal(false)}>Close</Button>
                      {!selectedUniversity.is_verified && (
                        <Button className="bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => {
                          reviewUniversity(selectedUniversity.id, 'approve');
                          setShowUniversityModal(false);
                        }}>
                          <ShieldCheck className="w-4 h-4 mr-2" />Approve & Verify
                        </Button>
                      )}
                      <Button variant="destructive" onClick={() => {
                        reviewUniversity(selectedUniversity.id, 'reject');
                        setShowUniversityModal(false);
                      }}>Reject</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Bank Transfer Details</CardTitle>
                    <CardDescription>Shown to users who choose "Pay by Bank Transfer" at checkout — include your account name, sort code/IBAN, and any reference instructions.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Textarea
                      value={bankTransferInstructions}
                      onChange={(e) => setBankTransferInstructions(e.target.value)}
                      placeholder="Account Name: WorksRun Ltd&#10;Sort Code: 00-00-00&#10;Account Number: 00000000&#10;Please use the payment reference shown to you at checkout."
                      rows={5}
                    />
                    <div className="flex justify-end">
                      <Button onClick={saveBankTransferInstructions} disabled={savingBankTransfer}>
                        {savingBankTransfer ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Save Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card><CardContent className="p-6 text-center">
                    <p className="text-sm text-muted-foreground">Total Transactions</p>
                    <p className="text-3xl font-bold text-[#003366] mt-1">{paymentsTotal}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-6 text-center">
                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                    <p className="text-3xl font-bold text-emerald-600 mt-1">£{totalRevenue.toFixed(2)}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-6 text-center">
                    <p className="text-sm text-muted-foreground">Avg Transaction</p>
                    <p className="text-3xl font-bold text-purple-600 mt-1">£{paymentsTotal > 0 ? (totalRevenue / paymentsTotal).toFixed(2) : '0.00'}</p>
                  </CardContent></Card>
                </div>
                <Card>
                  <CardHeader><CardTitle>Recent Transactions</CardTitle></CardHeader>
                  <CardContent>
                    {payments.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No transactions yet</p>
                    ) : (
                      <div className="space-y-2">
                        {payments.map(p => (
                          <div key={p.id} onClick={() => { setSelectedPayment(p); setShowPaymentModal(true); }} className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 cursor-pointer transition">
                            <div className="flex-1">
                              <p className="font-medium">{p.plan_name || p.service_name || 'Payment'}</p>
                              <p className="text-sm text-muted-foreground">{p.user?.email || p.metadata?.user_email || 'Unknown User'}</p>
                              <p className="text-xs text-muted-foreground">{p.created_at?.slice(0, 10)}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <span className="font-bold block">{formatCurrency(p.amount, p.currency)}</span>
                                <p className="text-xs text-muted-foreground">{p.user?.user_type || 'N/A'}</p>
                              </div>
                              <Badge className={getPaymentStatusColor(p.payment_status)}>{p.payment_status}</Badge>
                              {p.payment_status !== 'paid' && (
                                <Button
                                  variant="ghost" size="icon" className="text-red-500 hover:text-red-600"
                                  onClick={(e) => { e.stopPropagation(); deletePayment(p.id); }}
                                  title="Delete redundant/spam payment"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Payment Detail Modal */}
            {showPaymentModal && selectedPayment && (
              <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Payment Details</DialogTitle>
                    <DialogDescription>Complete transaction information for follow-up</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Service/Plan</p>
                        <p className="font-semibold text-lg">{selectedPayment.plan_name || selectedPayment.service_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Amount</p>
                        <p className="font-semibold text-lg text-emerald-600">{formatCurrency(selectedPayment.amount, selectedPayment.currency)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Date</p>
                        <p className="font-semibold">{new Date(selectedPayment.created_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge className={getPaymentStatusColor(selectedPayment.payment_status)}>{selectedPayment.payment_status}</Badge>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">User Information</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Name</p>
                          <p className="font-medium">{selectedPayment.user?.name || 'Unknown'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium text-blue-600 break-all">{selectedPayment.user?.email || selectedPayment.metadata?.user_email || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">User Type</p>
                          <p className="font-medium capitalize">{selectedPayment.user?.user_type || selectedPayment.metadata?.user_type || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">User ID</p>
                          <p className="font-mono text-xs">{selectedPayment.user_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Transaction Details</h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Session ID:</span><span className="font-mono">{selectedPayment.session_id}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Currency:</span><span>{selectedPayment.currency?.toUpperCase() || 'GBP'}</span></div>
                        {selectedPayment.subtotal !== undefined && <div className="flex justify-between"><span className="text-muted-foreground">Subtotal:</span><span>{formatCurrency(selectedPayment.subtotal, selectedPayment.currency)}</span></div>}
                        {selectedPayment.vat !== undefined && <div className="flex justify-between"><span className="text-muted-foreground">VAT (20%):</span><span>{formatCurrency(selectedPayment.vat, selectedPayment.currency)}</span></div>}
                        {selectedPayment.discount_amount !== undefined && <div className="flex justify-between"><span className="text-muted-foreground">Discount:</span><span>-{formatCurrency(selectedPayment.discount_amount, selectedPayment.currency)}</span></div>}
                        <div className="flex justify-between border-t pt-2"><span className="font-semibold">Total:</span><span className="font-bold">{formatCurrency(selectedPayment.amount, selectedPayment.currency)}</span></div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button onClick={() => { navigator.clipboard.writeText(selectedPayment.user?.email || 'N/A'); toast.success('Email copied to clipboard'); }}>
                        Copy Email
                      </Button>
                      {selectedPayment.payment_status !== 'paid' && (
                        <>
                          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approvePayment(selectedPayment.id)}>
                            <CheckCircle2 className="w-4 h-4 mr-2" />Approve Payment
                          </Button>
                          <Button variant="outline" onClick={() => sendPaymentReminder(selectedPayment.id)}>
                            Send Reminder
                          </Button>
                          <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => deletePayment(selectedPayment.id)}>
                            <Trash2 className="w-4 h-4 mr-2" />Delete
                          </Button>
                        </>
                      )}
                      <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="ml-auto">Close</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Recruitment Tab */}
            {activeTab === 'recruitment' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Direct Recruitment Requests ({recruitmentTotal})</h2>
                    <p className="text-sm text-muted-foreground mt-1">Income Stream 3: 15% commission on successful placements</p>
                  </div>
                  <a href="/recruitment-dashboard">
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <ArrowRight className="w-4 h-4 mr-2" />Full Dashboard
                    </Button>
                  </a>
                </div>
                {recruitmentRequests.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No recruitment requests yet.</p>
                      <p className="text-sm text-muted-foreground">Employers submit requests via the Recruiter page.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      {loading ? <Skeleton className="h-40 m-4" /> : (
                        <div className="divide-y">
                          {recruitmentRequests.map(req => (
                            <div key={req.id} onClick={() => { setSelectedRecruitment(req); setShowRecruitmentModal(true); }} className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition">
                              <div className="flex-1">
                                <p className="font-medium">{req.job_title || 'Recruitment Request'}</p>
                                <p className="text-sm text-muted-foreground">{req.company_name} • {req.location || 'Location N/A'} • {req.positions_count} position(s)</p>
                                <p className="text-xs text-muted-foreground">{req.created_at?.slice(0, 10)}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                {req.used_credit && <Badge className="bg-purple-100 text-purple-700">Paid</Badge>}
                                <Badge className={{
                                  pending: 'bg-amber-100 text-amber-700',
                                  in_progress: 'bg-blue-100 text-blue-700',
                                  completed: 'bg-emerald-100 text-emerald-700',
                                  cancelled: 'bg-red-100 text-red-700',
                                }[req.status] || 'bg-gray-100 text-gray-700'}>
                                  {req.status?.replace('_', ' ')}
                                </Badge>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updateRecruitmentStatus(req.id, 'in_progress'); }}>
                                      <Clock className="w-4 h-4 mr-2" />Mark In Progress
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updateRecruitmentStatus(req.id, 'completed'); }}>
                                      <CheckCircle2 className="w-4 h-4 mr-2" />Mark Completed
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updateRecruitmentStatus(req.id, 'cancelled'); }} className="text-red-600">
                                      <XCircle className="w-4 h-4 mr-2" />Cancel
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Recruitment Detail Modal */}
            {showRecruitmentModal && selectedRecruitment && (
              <Dialog open={showRecruitmentModal} onOpenChange={setShowRecruitmentModal}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Recruitment Request Details</DialogTitle>
                    <DialogDescription>Complete information for direct recruitment service request</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    {/* Header Info */}
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Job Title</p>
                        <p className="font-semibold text-lg">{selectedRecruitment.job_title}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Company</p>
                        <p className="font-semibold">{selectedRecruitment.company_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge className={{
                          pending: 'bg-amber-100 text-amber-700',
                          in_progress: 'bg-blue-100 text-blue-700',
                          completed: 'bg-emerald-100 text-emerald-700',
                          cancelled: 'bg-red-100 text-red-700',
                        }[selectedRecruitment.status] || 'bg-gray-100 text-gray-700'}>
                          {selectedRecruitment.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>

                    {/* Key Details */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Position Details</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Location</p>
                          <p className="font-medium">{selectedRecruitment.location}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Positions</p>
                          <p className="font-medium">{selectedRecruitment.positions_count}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Payment</p>
                          <p className="font-medium">{selectedRecruitment.used_credit ? 'Paid (credit used)' : 'Free inquiry'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Salary Range</p>
                          <p className="font-medium">{selectedRecruitment.salary_range || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Urgency</p>
                          <Badge variant="outline" className="capitalize">{selectedRecruitment.urgency}</Badge>
                        </div>
                      </div>
                    </div>

                    {/* Job Description */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-2">Job Description</h3>
                      <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedRecruitment.job_description}</p>
                    </div>

                    {/* Requirements */}
                    {selectedRecruitment.requirements && selectedRecruitment.requirements.length > 0 && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-4">Requirements</h3>
                        <ul className="list-disc list-inside space-y-2">
                          {selectedRecruitment.requirements.map((req, idx) => (
                            <li key={idx} className="text-sm">{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Status Info */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Recruitment Status</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Candidates Submitted</p>
                          <p className="font-bold text-2xl">{selectedRecruitment.candidates_submitted || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Commission</p>
                          <p className="font-bold text-lg">{selectedRecruitment.fee_percentage}%</p>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedRecruitment.notes && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-2">Additional Notes</h3>
                        <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedRecruitment.notes}</p>
                      </div>
                    )}

                    {/* Timeline */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Timeline</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Submitted</p>
                          <p className="font-medium">{new Date(selectedRecruitment.created_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Last Updated</p>
                          <p className="font-medium">{new Date(selectedRecruitment.updated_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowRecruitmentModal(false)}>Close</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Student Services Tab */}
            {activeTab === 'student_services' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between pt-4">
                  <div>
                    <h2 className="text-xl font-semibold">Student Applications ({studentTotal})</h2>
                    <p className="text-sm text-muted-foreground mt-1">Income Stream 4: University recruitment & visa assistance</p>
                  </div>
                </div>
                {studentApplications.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No student applications yet.</p>
                      <p className="text-sm text-muted-foreground">Students apply via the Student Services page.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      {loading ? <Skeleton className="h-40 m-4" /> : (
                        <div className="divide-y">
                          {studentApplications.map(app => (
                            <div key={app.id} className="flex items-center justify-between p-4">
                              <div>
                                <p className="font-medium">{app.program_name || 'Programme Application'}</p>
                                <p className="text-sm text-muted-foreground">
                                  {app.university_name} • {app.visa_required ? `Visa: ${app.visa_type}` : 'No visa required'}
                                </p>
                                <p className="text-xs text-muted-foreground">{app.created_at?.slice(0, 10)}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                {app.visa_required && (
                                  <Badge className="bg-amber-100 text-amber-700">Visa Required</Badge>
                                )}
                                <Badge className={{
                                  pending: 'bg-amber-100 text-amber-700',
                                  approved: 'bg-emerald-100 text-emerald-700',
                                  rejected: 'bg-red-100 text-red-700',
                                  under_review: 'bg-blue-100 text-blue-700',
                                }[app.status] || 'bg-gray-100 text-gray-700'}>
                                  {app.status?.replace('_', ' ')}
                                </Badge>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => updateStudentAppStatus(app.id, 'under_review')}>
                                      <Eye className="w-4 h-4 mr-2" />Under Review
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => updateStudentAppStatus(app.id, 'approved')}>
                                      <CheckCircle2 className="w-4 h-4 mr-2" />Approve
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => updateStudentAppStatus(app.id, 'rejected')} className="text-red-600">
                                      <XCircle className="w-4 h-4 mr-2" />Reject
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Revenue Tab */}
            {activeTab === 'revenue' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">Revenue Analytics</h2>
                  <p className="text-sm text-muted-foreground mt-1">Breakdown across all 4 income streams</p>
                </div>
                {loading ? <Skeleton className="h-64" /> : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      {[
                        { label: 'Job Postings', value: revenueData?.by_stream?.job_postings || 0, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: Briefcase, stream: 1 },
                        { label: 'Course Listings', value: revenueData?.by_stream?.courses || 0, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: GraduationCap, stream: 2 },
                        { label: 'Direct Recruitment', value: revenueData?.by_stream?.direct_recruitment || 0, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200', icon: Building2, stream: 3 },
                        { label: 'Student Services', value: revenueData?.by_stream?.student_services || 0, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Globe, stream: 4 },
                        { label: 'PPC Campaigns', value: revenueData?.by_stream?.ppc || 0, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200', icon: TrendingUp, stream: 5 },
                      ].map((s, i) => (
                        <Card key={i} className={`border-2 ${s.bg}`}>
                          <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <s.icon className={`w-5 h-5 ${s.color}`} />
                              <span className="text-xs font-medium text-muted-foreground">STREAM {s.stream}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{s.label}</p>
                            <p className={`text-2xl font-bold ${s.color}`}>£{s.value.toFixed(2)}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5" />Total Platform Revenue</CardTitle></CardHeader>
                        <CardContent>
                          <p className="text-5xl font-bold text-[#003366]">
                            £{(revenueData?.total_revenue || 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">Across all payment streams (Stripe)</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader><CardTitle>Activity Counts</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                          {[
                            { label: 'Job Applications', value: revenueData?.counts?.job_applications || 0, icon: Briefcase },
                            { label: 'Course Enrollments', value: revenueData?.counts?.course_enrollments || 0, icon: GraduationCap },
                            { label: 'Recruitment Requests', value: revenueData?.counts?.recruitment_requests || 0, icon: Building2 },
                            { label: 'Student Applications', value: revenueData?.counts?.student_applications || 0, icon: Globe },
                          ].map((item, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                                <item.icon className="w-4 h-4" />{item.label}
                              </span>
                              <span className="font-bold text-[#003366]">{item.value.toLocaleString()}</span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>

                    {revenueData?.by_plan && Object.keys(revenueData.by_plan).length > 0 && (
                      <Card>
                        <CardHeader><CardTitle>Revenue by Plan</CardTitle></CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {Object.entries(revenueData.by_plan).map(([plan, data], i) => (
                              <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                                <div>
                                  <p className="font-medium capitalize">{plan?.replace(/_/g, ' ') || 'Unknown'}</p>
                                  <p className="text-sm text-muted-foreground">{data.count} transaction{data.count !== 1 ? 's' : ''}</p>
                                </div>
                                <span className="font-bold text-emerald-600">£{data.total?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div className="space-y-6">
                <div className="flex justify-end">
                  <Button onClick={() => { setEditingPlan(null); setPlanForm(EMPTY_PLAN_FORM); setShowPlanModal(true); }}
                    className="bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="add-plan-btn">
                    <Plus className="w-4 h-4 mr-2" />Add Plan
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {plans.map(p => (
                    <Card key={p.id} data-testid={`plan-admin-${p.slug}`}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>{p.name}</CardTitle>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditPlan(p)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => deletePlan(p.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <CardDescription>{p.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-[#003366] mb-1">{p.price === 0 ? 'Free' : `£${p.price}/${p.billing_cycle === 'annual' ? 'yr' : 'mo'}`}</p>
                        <p className="text-xs text-muted-foreground mb-4">{(p.role || 'employer').replace('_', ' ')} · {p.billing_cycle || 'monthly'}</p>
                        <ul className="space-y-2">
                          {(p.features || []).map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{f}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-4 flex items-center gap-2">
                          <Badge className={p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                            {p.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            {activeTab === 'ai_credits' && <AiCreditsPanel />}
            {/* Advertisements Tab */}
            {activeTab === 'advertisements' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-xl font-semibold">Advertisement Blocks</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Manage ads shown across the site. Supports HTML, Google AdSense, image banners and text banners.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" onClick={seedPlaceholderAds} disabled={seedingAds}>
                      {seedingAds ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Megaphone className="w-4 h-4 mr-2" />}
                      Seed Placeholder Ads
                    </Button>
                    <Button onClick={openNewAd} className="bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="add-ad-btn">
                      <Plus className="w-4 h-4 mr-2" />New Ad Block
                    </Button>
                  </div>
                </div>

                {/* Size reference */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold text-blue-800 mb-2">Standard IAB Ad Sizes</p>
                    <div className="flex flex-wrap gap-2">
                      {AD_SIZES.map(s => (
                        <span key={s.value} className="text-xs bg-white border border-blue-200 text-blue-700 px-2 py-1 rounded">{s.label}</span>
                      ))}
                    </div>
                    <p className="text-xs text-blue-600 mt-2">Click <strong>Seed Placeholder Ads</strong> to insert grey placeholder blocks in every position so you can preview the layout.</p>
                  </CardContent>
                </Card>

                {/* Empty state */}
                {ads.length === 0 && !loading && (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold text-lg mb-1">No ad blocks yet</h3>
                      <p className="text-muted-foreground mb-4">Create your first ad block to start showing promotions on the site.</p>
                      <Button onClick={openNewAd} className="bg-[#E11D48] hover:bg-[#E11D48]/90">
                        <Plus className="w-4 h-4 mr-2" />Create Ad Block
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Group by position */}
                {AD_POSITIONS.map(pos => {
                  const posAds = ads.filter(a => a.position === pos.value);
                  if (posAds.length === 0) return null;
                  return (
                    <div key={pos.value}>
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Megaphone className="w-4 h-4" />
                        {pos.label} <span className="text-xs font-normal bg-gray-100 px-2 py-0.5 rounded-full">{posAds.length}</span>
                      </h3>
                      <div className="space-y-2">
                        {posAds.map(ad => (
                          <div key={ad.id} className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${ad.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                            <div className="flex items-center gap-4 min-w-0">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${ad.ad_type === 'adsense' ? 'bg-blue-100' : ad.ad_type === 'image' ? 'bg-purple-100' : ad.ad_type === 'text' ? 'bg-amber-100' : 'bg-gray-100'}`}>
                                <Code className={`w-4 h-4 ${ad.ad_type === 'adsense' ? 'text-blue-600' : ad.ad_type === 'image' ? 'text-purple-600' : ad.ad_type === 'text' ? 'text-amber-600' : 'text-gray-600'}`} />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{ad.name || 'Untitled Ad'}{ad.is_placeholder && <span className="ml-2 text-xs text-amber-600 font-normal">(placeholder)</span>}</p>
                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                  <Badge className={`text-xs ${ad.ad_type === 'adsense' ? 'bg-blue-100 text-blue-700' : ad.ad_type === 'image' ? 'bg-purple-100 text-purple-700' : ad.ad_type === 'text' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>
                                    {AD_TYPES.find(t => t.value === ad.ad_type)?.label || ad.ad_type}
                                  </Badge>
                                  {ad.ad_size && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">{ad.ad_size}</span>}
                                  <span className="text-xs text-muted-foreground">Priority: {ad.priority}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => toggleAd(ad.id)}
                                className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-colors ${ad.is_active ? 'text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100' : 'text-gray-500 border-gray-200 bg-gray-50 hover:bg-gray-100'}`}
                              >
                                {ad.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                                {ad.is_active ? 'Live' : 'Off'}
                              </button>
                              <Button variant="ghost" size="icon" onClick={() => openEditAd(ad)} className="h-8 w-8">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => deleteAd(ad.id)} className="h-8 w-8 text-red-500 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {ads.length === 0 && !loading && (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold text-lg mb-2">No ad blocks yet</h3>
                      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                        Create ad blocks to monetise your platform. Paste HTML embed codes, Google AdSense snippets,
                        image banners or simple text banners into any page position.
                      </p>
                      <Button onClick={openNewAd} className="bg-[#E11D48] hover:bg-[#E11D48]/90">
                        <Plus className="w-4 h-4 mr-2" />Create First Ad Block
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Payment Gateways Tab */}
            {activeTab === 'payment_gateways' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">Payment Gateway Configuration</h2>
                  <p className="text-sm text-muted-foreground mt-1">Configure and manage payment processors for subscriptions and transactions.</p>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {gateways.length === 0 && loading && <Skeleton className="h-48" />}
                  {gateways.map(gw => (
                    <Card key={gw.id} className={`border-2 ${gw.is_enabled ? 'border-emerald-200' : 'border-gray-200'}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white text-sm ${gw.gateway === 'stripe' ? 'bg-indigo-600' : gw.gateway === 'paypal' ? 'bg-blue-500' : 'bg-gray-800'}`}>
                              {gw.display_name?.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{gw.display_name}</h3>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge className={gw.is_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                                  {gw.is_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                                <Badge className={gw.is_test_mode ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                                  {gw.is_test_mode ? 'Test Mode' : 'Live Mode'}
                                </Badge>
                                {gatewayTestResult[gw.id] && (
                                  <Badge className={gatewayTestResult[gw.id].status === 'connected' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                                    {gatewayTestResult[gw.id].status === 'connected' ? '✓ Connected' : `✗ ${gatewayTestResult[gw.id].status === 'not_configured' ? 'Not configured' : 'Failed'}`}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => testGateway(gw.id)} disabled={testingGateway === gw.id}>
                              {testingGateway === gw.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                              Test
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => toggleGateway(gw)}>
                              {gw.is_enabled ? <Lock className="w-4 h-4 mr-1" /> : <Unlock className="w-4 h-4 mr-1" />}
                              {gw.is_enabled ? 'Disable' : 'Enable'}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setEditingGateway(gw); setGatewayForm({ public_key: gw.public_key || '', secret_key: '', webhook_url: gw.webhook_url || '', transaction_fee_percent: gw.transaction_fee_percent, transaction_fee_fixed: gw.transaction_fee_fixed }); }}>
                              <Edit className="w-4 h-4 mr-1" />Configure
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-muted-foreground mb-1">Public Key</p>
                            <p className="text-sm font-mono truncate">{gw.public_key || <span className="text-muted-foreground italic">Not set</span>}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-muted-foreground mb-1">Secret Key</p>
                            <p className="text-sm font-mono">{gw.secret_key_masked || <span className="text-muted-foreground italic">Not set</span>}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-muted-foreground mb-1">Transaction Fee</p>
                            <p className="text-sm font-medium">{gw.transaction_fee_percent}% + £{gw.transaction_fee_fixed}</p>
                          </div>
                        </div>

                        {editingGateway?.id === gw.id && (
                          <div className="mt-4 p-4 border rounded-lg bg-blue-50 space-y-3">
                            <h4 className="font-medium">Configure {gw.display_name}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Public Key</Label>
                                <Input value={gatewayForm.public_key} onChange={e => setGatewayForm({...gatewayForm, public_key: e.target.value})} placeholder="pk_test_..." className="font-mono text-sm" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Secret Key (will be masked)</Label>
                                <Input type="password" value={gatewayForm.secret_key} onChange={e => setGatewayForm({...gatewayForm, secret_key: e.target.value})} placeholder="sk_test_..." className="font-mono text-sm" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Webhook URL</Label>
                                <Input value={gatewayForm.webhook_url} onChange={e => setGatewayForm({...gatewayForm, webhook_url: e.target.value})} placeholder="https://yourdomain.com/api/webhooks/stripe" />
                              </div>
                              {gw.gateway === 'stripe' && (
                                <div className="space-y-1">
                                  <Label className="text-xs">Webhook signing secret (whsec_…)</Label>
                                  <Input type="password" value={gatewayForm.webhook_secret || ''} onChange={e => setGatewayForm({...gatewayForm, webhook_secret: e.target.value})} placeholder={gw.webhook_secret_masked ? `Saved (${gw.webhook_secret_masked}) — enter to replace` : 'whsec_...'} className="font-mono text-sm" />
                                </div>
                              )}
                              <div className="space-y-1">
                                <Label className="text-xs">Transaction Fee %</Label>
                                <Input type="number" step="0.1" value={gatewayForm.transaction_fee_percent} onChange={e => setGatewayForm({...gatewayForm, transaction_fee_percent: e.target.value})} />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-2">
                              <Button variant="outline" size="sm" onClick={() => setEditingGateway(null)}>Cancel</Button>
                              <Button size="sm" className="bg-[#003366]" onClick={saveGateway}>Save Configuration</Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <PaymentSettingsPanel />

                {/* Test mode warning */}
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Test Mode Active</p>
                      <p className="text-sm text-amber-700 mt-0.5">Payment gateways are in test mode. No real charges will be made. Switch to live mode only when ready for production.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Content Moderation Tab */}
            {activeTab === 'moderation' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Content Moderation</h2>
                    <p className="text-sm text-muted-foreground mt-1">Review and approve pending content before it goes live.</p>
                  </div>
                  <Badge className="bg-red-100 text-red-700 text-base px-3 py-1">
                    {moderationQueue.total} pending
                  </Badge>
                </div>

                {/* Filter buttons */}
                <div className="flex gap-2 flex-wrap">
                  {['all', 'job', 'course', 'review', 'event'].map(f => (
                    <Button key={f} variant={moderationFilter === f ? 'default' : 'outline'} size="sm"
                      onClick={() => setModerationFilter(f)}
                      className={moderationFilter === f ? 'bg-[#003366]' : ''}>
                      {f === 'all' ? 'All Content' : f.charAt(0).toUpperCase() + f.slice(1) + 's'}
                      {f !== 'all' && <Badge className="ml-1 bg-gray-200 text-gray-700 text-xs">{moderationQueue[f + 's']?.length || 0}</Badge>}
                    </Button>
                  ))}
                </div>

                {/* Pending Jobs */}
                {(moderationFilter === 'all' || moderationFilter === 'job') && moderationQueue.jobs?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-4 h-4" />Pending Jobs ({moderationQueue.jobs.length})</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {moderationQueue.jobs.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-4">
                            <div>
                              <p className="font-medium">{item.title}</p>
                              <p className="text-sm text-muted-foreground">{item.company_name} • {item.location}</p>
                              <p className="text-xs text-muted-foreground">{item.created_at?.slice(0, 10)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openModerateModal(item, 'job', 'approve')}>
                                <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => openModerateModal(item, 'job', 'reject')}>
                                <XCircle className="w-4 h-4 mr-1" />Reject
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Pending Courses */}
                {(moderationFilter === 'all' || moderationFilter === 'course') && moderationQueue.courses?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><GraduationCap className="w-4 h-4" />Pending Courses ({moderationQueue.courses.length})</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {moderationQueue.courses.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-4">
                            <div>
                              <p className="font-medium">{item.title}</p>
                              <p className="text-sm text-muted-foreground">{item.institution_name} • {item.category}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openModerateModal(item, 'course', 'approve')}>
                                <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => openModerateModal(item, 'course', 'reject')}>
                                <XCircle className="w-4 h-4 mr-1" />Reject
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Pending Reviews */}
                {(moderationFilter === 'all' || moderationFilter === 'review') && moderationQueue.reviews?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Eye className="w-4 h-4" />Unverified Reviews ({moderationQueue.reviews.length})</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {moderationQueue.reviews.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">{item.user_name || 'Anonymous'}</span>
                                <span className="text-amber-500">{'★'.repeat(item.rating)}</span>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">{item.content}</p>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openModerateModal(item, 'review', 'approve')}>
                                Verify
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => openModerateModal(item, 'review', 'remove')}>
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {moderationQueue.total === 0 && !loading && (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                      <h3 className="font-semibold text-lg mb-1">All Clear!</h3>
                      <p className="text-muted-foreground">No content pending moderation.</p>
                    </CardContent>
                  </Card>
                )}

                {/* Moderation Log */}
                {moderationLogs.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Recent Moderation Actions</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y max-h-64 overflow-y-auto">
                        {moderationLogs.slice(0, 20).map(log => (
                          <div key={log.id} className="flex items-center justify-between p-3 text-sm">
                            <div>
                              <span className="font-medium">{log.admin_name}</span>
                              <span className="text-muted-foreground"> {log.action} </span>
                              <span className="font-medium">{log.target_title || log.target_id}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {log.reason && <span className="text-xs text-muted-foreground italic">{log.reason}</span>}
                              <span className="text-xs text-muted-foreground">{log.created_at?.slice?.(0, 10) || ''}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Role Management Tab */}
            {activeTab === 'role_management' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">User Role Management</h2>
                  <p className="text-sm text-muted-foreground mt-1">View and change user roles, suspend accounts, and manage permissions.</p>
                </div>

                {/* Permissions Matrix */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Role Permissions Matrix</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 pr-4 font-semibold">Role</th>
                            <th className="text-left py-2 font-semibold">Permissions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {Object.entries(permissionsMatrix).map(([role, perms]) => (
                            <tr key={role} className="hover:bg-gray-50">
                              <td className="py-3 pr-4">
                                <Badge className={`${getUserTypeColor(role)} capitalize`}>{role.replace('_', ' ')}</Badge>
                              </td>
                              <td className="py-3">
                                <div className="flex flex-wrap gap-1">
                                  {(Array.isArray(perms) ? perms : []).map(perm => (
                                    <span key={perm} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono">
                                      {perm}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* User Role Search & Change */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Manage User Roles</CardTitle>
                      <Input
                        placeholder="Search by name or email..."
                        value={roleSearchTerm}
                        onChange={e => setRoleSearchTerm(e.target.value)}
                        className="w-64"
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-[500px] overflow-y-auto">
                      {roleUsers
                        .filter(u => !roleSearchTerm || u.full_name?.toLowerCase().includes(roleSearchTerm.toLowerCase()) || u.email?.toLowerCase().includes(roleSearchTerm.toLowerCase()))
                        .map(u => (
                          <div key={u.id} className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-[#003366] flex items-center justify-center text-white text-sm font-semibold">
                                {u.full_name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium">{u.full_name}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {u.is_suspended && <Badge className="bg-red-100 text-red-700">Suspended</Badge>}
                              <Badge className={`${getUserTypeColor(u.user_type)} capitalize`}>{u.user_type?.replace('_', ' ')}</Badge>
                              <Button variant="outline" size="sm" onClick={() => openRoleModal(u)}>
                                <UserCog className="w-4 h-4 mr-1" />Change Role
                              </Button>
                              <Button variant="outline" size="sm"
                                className={u.is_suspended ? 'text-emerald-600 border-emerald-200' : 'text-red-600 border-red-200'}
                                onClick={() => suspendUser(u.id, u.is_suspended)}>
                                {u.is_suspended ? <><ShieldCheck className="w-4 h-4 mr-1" />Unsuspend</> : <><ShieldOff className="w-4 h-4 mr-1" />Suspend</>}
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Discount Codes Tab */}
            {activeTab === 'discount_codes' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Discount Codes</h2>
                    <p className="text-sm text-muted-foreground mt-1">Create and manage promotional discount codes for subscription plans.</p>
                  </div>
                  <Button onClick={() => { setDiscountForm({ code: '', discount_type: 'percent', discount_value: 10, applicable_plans: [], max_uses: '', valid_until: '' }); setShowDiscountModal(true); }} className="bg-[#E11D48] hover:bg-[#E11D48]/90">
                    <Plus className="w-4 h-4 mr-2" />Create Code
                  </Button>
                </div>

                {discountCodes.length === 0 && !loading ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold text-lg mb-1">No discount codes</h3>
                      <p className="text-muted-foreground">Create your first promotional code to offer discounts on subscription plans.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {discountCodes.map(dc => (
                          <div key={dc.id} className={`flex items-center justify-between p-4 ${!dc.is_active ? 'opacity-50' : ''}`}>
                            <div className="flex items-center gap-4">
                              <div className="bg-[#003366]/10 rounded-lg px-3 py-2 font-mono font-bold text-[#003366] text-sm">
                                {dc.code}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-purple-100 text-purple-700">
                                    {dc.discount_type === 'percent' ? `${dc.discount_value}% off` : `£${dc.discount_value} off`}
                                  </Badge>
                                  <Badge className={dc.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                                    {dc.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Used: {dc.uses_count}{dc.max_uses ? ` / ${dc.max_uses}` : ' (unlimited)'}
                                  {dc.valid_until && ` • Expires: ${dc.valid_until?.slice?.(0, 10) || ''}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={() => toggleDiscountCode(dc.id)}>
                                {dc.is_active ? <Lock className="w-4 h-4 mr-1" /> : <Unlock className="w-4 h-4 mr-1" />}
                                {dc.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => deleteDiscountCode(dc.id)} className="text-red-500 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

      {/* Ad Block Modal */}
      {/* Event Modal */}
      {showEventModal && (
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
            <DialogDescription>Fill in the details for this event. Required fields are marked.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Event Title *</Label>
                <Input placeholder="e.g. International Education Conference 2026" value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea rows={3} placeholder="Describe the event…" value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Event Type</Label>
                <Select value={eventForm.event_type} onValueChange={v => setEventForm({...eventForm, event_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['conference', 'workshop', 'webinar', 'fair', 'networking', 'career_fair', 'other'].map(t => (
                      <SelectItem key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={eventForm.status} onValueChange={v => setEventForm({...eventForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['active', 'draft', 'cancelled', 'completed'].map(s => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date & Time *</Label>
                <Input type="datetime-local" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>End Date & Time</Label>
                <Input type="datetime-local" value={eventForm.end_date} onChange={e => setEventForm({...eventForm, end_date: e.target.value})} />
              </div>
              <div className="space-y-2 col-span-2">
                <div className="flex items-center gap-3">
                  <Switch checked={eventForm.is_virtual} onCheckedChange={v => setEventForm({...eventForm, is_virtual: v})} />
                  <Label>Virtual / Online Event</Label>
                </div>
              </div>
              {eventForm.is_virtual ? (
                <div className="space-y-2 col-span-2">
                  <Label>Virtual Event Link</Label>
                  <Input placeholder="https://zoom.us/j/..." value={eventForm.virtual_link} onChange={e => setEventForm({...eventForm, virtual_link: e.target.value})} />
                </div>
              ) : (
                <div className="space-y-2 col-span-2">
                  <Label>Location</Label>
                  <Input placeholder="e.g. London ExCeL Centre" value={eventForm.location} onChange={e => setEventForm({...eventForm, location: e.target.value})} />
                </div>
              )}
              <div className="space-y-2">
                <Label>Organiser Name</Label>
                <Input placeholder="e.g. UK Education Board" value={eventForm.organizer_name} onChange={e => setEventForm({...eventForm, organizer_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Max Attendees</Label>
                <Input type="number" placeholder="Leave blank for unlimited" value={eventForm.max_attendees} onChange={e => setEventForm({...eventForm, max_attendees: e.target.value})} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Registration / External URL</Label>
                <Input placeholder="https://eventbrite.com/..." value={eventForm.registration_url} onChange={e => setEventForm({...eventForm, registration_url: e.target.value})} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Cover Image URL</Label>
                <Input placeholder="https://example.com/event-banner.jpg" value={eventForm.image_url} onChange={e => setEventForm({...eventForm, image_url: e.target.value})} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Tags (comma-separated)</Label>
                <Input placeholder="e.g. education, uk, scholarship" value={eventForm.tags} onChange={e => setEventForm({...eventForm, tags: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>Cancel</Button>
            <Button onClick={saveEvent} className="bg-[#003366] hover:bg-[#003366]/90">
              {editingEvent ? 'Update Event' : 'Create Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {showAdModal && (
      <Dialog open={showAdModal} onOpenChange={setShowAdModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAd ? 'Edit Ad Block' : 'Create Ad Block'}</DialogTitle>
            <DialogDescription>
              Configure where and how this advertisement appears on the site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Ad Name (internal label)</Label>
                <Input
                  placeholder="e.g. Google AdSense Header 728x90"
                  value={adForm.name}
                  onChange={e => setAdForm({ ...adForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Position</Label>
                <Select value={adForm.position} onValueChange={v => setAdForm({ ...adForm, position: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AD_POSITIONS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}{p.size ? ` (${p.size})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ad Type</Label>
                <Select value={adForm.ad_type} onValueChange={v => setAdForm({ ...adForm, ad_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AD_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Ad Size</Label>
                <Select value={adForm.ad_size || '728x90'} onValueChange={v => setAdForm({ ...adForm, ad_size: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AD_SIZES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Content field — label changes based on type */}
            <div className="space-y-2">
              <Label>
                {adForm.ad_type === 'html' && 'HTML / Embed Code'}
                {adForm.ad_type === 'adsense' && 'Google AdSense Script Code'}
                {adForm.ad_type === 'image' && 'Image URL or Upload'}
                {adForm.ad_type === 'text' && 'Banner Content (JSON)'}
              </Label>
              {adForm.ad_type === 'image' ? (
                <div className="space-y-2">
                  <Input
                    placeholder="https://example.com/banner.jpg"
                    value={adForm.content}
                    onChange={e => setAdForm({ ...adForm, content: e.target.value })}
                  />
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={e => uploadAdImage(e.target.files[0])} />
                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
                        {uploadingAdImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        {uploadingAdImage ? 'Uploading…' : 'Upload Image'}
                      </span>
                    </label>
                    <span className="text-xs text-muted-foreground">JPG, PNG, GIF, WebP — max 5MB</span>
                  </div>
                  {adForm.content && adForm.content.startsWith('data:image') && (
                    <img src={adForm.content} alt="Preview" className="max-h-24 rounded border" />
                  )}
                </div>
              ) : adForm.ad_type === 'text' ? (
                <>
                  <Textarea
                    rows={4}
                    placeholder={`{"heading": "Special Offer!", "body": "Save 50% today", "cta_label": "Shop Now", "bg_color": "#003366", "text_color": "#ffffff"}`}
                    value={adForm.content}
                    onChange={e => setAdForm({ ...adForm, content: e.target.value })}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground">JSON with keys: heading, body, cta_label, bg_color, text_color</p>
                </>
              ) : (
                <Textarea
                  rows={6}
                  placeholder={adForm.ad_type === 'adsense'
                    ? '<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXXXXXXXXXXXXXXX" crossorigin="anonymous"></script>\n<ins class="adsbygoogle" ...'
                    : '<div>Your custom HTML or embed code here</div>'}
                  value={adForm.content}
                  onChange={e => setAdForm({ ...adForm, content: e.target.value })}
                  className="font-mono text-xs"
                />
              )}
            </div>

            {/* Link URL — for image and text types */}
            {(adForm.ad_type === 'image' || adForm.ad_type === 'text') && (
              <div className="space-y-2">
                <Label>Click-through URL (optional)</Label>
                <Input
                  placeholder="https://advertiser-website.com"
                  value={adForm.link_url}
                  onChange={e => setAdForm({ ...adForm, link_url: e.target.value })}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority (lower = shown first)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={adForm.priority}
                  onChange={e => setAdForm({ ...adForm, priority: e.target.value })}
                />
              </div>
              <div className="space-y-2 flex items-end">
                <div className="flex items-center gap-3 pb-1">
                  <Switch
                    checked={adForm.is_active}
                    onCheckedChange={v => setAdForm({ ...adForm, is_active: v })}
                  />
                  <Label>{adForm.is_active ? 'Live (visible to visitors)' : 'Draft (hidden)'}</Label>
                </div>
              </div>
            </div>

            {/* Preview for text type */}
            {adForm.ad_type === 'text' && adForm.content && (() => {
              try {
                const d = JSON.parse(adForm.content);
                return (
                  <div>
                    <Label>Preview</Label>
                    <div className="mt-2 rounded-lg overflow-hidden border">
                      <div className="py-3 px-6 flex items-center justify-between gap-3 text-sm" style={{ backgroundColor: d.bg_color || '#003366', color: d.text_color || '#fff' }}>
                        <div>
                          {d.heading && <span className="font-semibold mr-3">{d.heading}</span>}
                          {d.body && <span className="opacity-80">{d.body}</span>}
                        </div>
                        {d.cta_label && <span className="px-4 py-1.5 rounded-full text-xs font-semibold border border-white/40">{d.cta_label}</span>}
                      </div>
                    </div>
                  </div>
                );
              } catch { return null; }
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdModal(false)}>Cancel</Button>
            <Button onClick={saveAd} className="bg-[#003366] hover:bg-[#003366]/90">
              {editingAd ? 'Update Ad' : 'Create Ad'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {accessUser && (
        <UserAccessDialog user={accessUser} countries={countries.filter(c => c.is_active !== false)}
          onClose={() => setAccessUser(null)} onSaved={fetchData} />
      )}

      {/* Plan Modal */}
      {showPlanModal && (
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
            <DialogDescription>Configure subscription plan details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label><Input value={planForm.name} onChange={e => setPlanForm({ ...planForm, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Slug</Label><Input value={planForm.slug} onChange={e => setPlanForm({ ...planForm, slug: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plan for</Label>
                <Select value={planForm.role} onValueChange={v => setPlanForm({ ...planForm, role: v })}>
                  <SelectTrigger data-testid="plan-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(planMeta?.roles || ['employer', 'course_provider', 'event_host', 'university_manager']).map(r => (
                      <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Billing cycle</Label>
                <Select value={planForm.billing_cycle} onValueChange={v => setPlanForm({ ...planForm, billing_cycle: v })}>
                  <SelectTrigger data-testid="plan-cycle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Price (£ per {planForm.billing_cycle === 'annual' ? 'year' : 'month'})</Label><Input type="number" value={planForm.price} onChange={e => setPlanForm({ ...planForm, price: e.target.value })} /></div>
              <div className="space-y-2"><Label>Sort Order</Label><Input type="number" value={planForm.sort_order} onChange={e => setPlanForm({ ...planForm, sort_order: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={planForm.description} onChange={e => setPlanForm({ ...planForm, description: e.target.value })} /></div>
            <div className="space-y-2"><Label>Features (one per line)</Label><Textarea value={planForm.features} onChange={e => setPlanForm({ ...planForm, features: e.target.value })} rows={4} /></div>
            <PlanLimitsEditor limits={planForm.limits} meta={planMeta} onChange={limits => setPlanForm({ ...planForm, limits })} />
            <div className="flex items-center gap-3">
              <Switch checked={planForm.is_active} onCheckedChange={v => setPlanForm({ ...planForm, is_active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>Cancel</Button>
            <Button onClick={savePlan} className="bg-[#003366] hover:bg-[#003366]/90">Save Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Moderation Action Modal */}
      {showModerationModal && (
      <Dialog open={showModerationModal} onOpenChange={setShowModerationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">{moderationAction} Content</DialogTitle>
            <DialogDescription>
              {moderatingItem && `${moderationAction === 'approve' ? 'Approve' : moderationAction === 'reject' ? 'Reject' : 'Remove'} "${moderatingItem.title || moderatingItem.content?.slice(0, 50)}"`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason {moderationAction !== 'approve' && <span className="text-red-500">*</span>}</Label>
              <Textarea
                value={moderationReason}
                onChange={e => setModerationReason(e.target.value)}
                placeholder={moderationAction === 'approve' ? 'Optional note...' : 'Explain the reason for this action...'}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModerationModal(false)}>Cancel</Button>
            <Button
              onClick={submitModerationAction}
              className={moderationAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              disabled={moderationAction !== 'approve' && !moderationReason.trim()}
            >
              Confirm {moderationAction?.charAt(0).toUpperCase() + moderationAction?.slice(1)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Role Change Modal */}
      {showRoleModal && (
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>{editingUserRole?.full_name} ({editingUserRole?.email})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['job_seeker', 'employer', 'course_provider', 'recruiter', 'student', 'university_manager', 'event_host', 'regular', 'admin'].map(r => (
                    <SelectItem key={r} value={r} className="capitalize">{r.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason for Change</Label>
              <Textarea value={roleChangeReason} onChange={e => setRoleChangeReason(e.target.value)} placeholder="Why is this role being changed?" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleModal(false)}>Cancel</Button>
            <Button onClick={saveRoleChange} className="bg-[#003366]">Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Create Discount Code Modal */}
      {showDiscountModal && (
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Discount Code</DialogTitle>
            <DialogDescription>Offer promotional discounts on subscription plans</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Code <span className="text-red-500">*</span></Label>
                <Input value={discountForm.code} onChange={e => setDiscountForm({...discountForm, code: e.target.value.toUpperCase()})} placeholder="SUMMER25" className="font-mono uppercase" />
              </div>
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select value={discountForm.discount_type} onValueChange={v => setDiscountForm({...discountForm, discount_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Discount Value</Label>
                <Input type="number" value={discountForm.discount_value} onChange={e => setDiscountForm({...discountForm, discount_value: e.target.value})} placeholder={discountForm.discount_type === 'percent' ? '25' : '10'} />
              </div>
              <div className="space-y-2">
                <Label>Max Uses (blank = unlimited)</Label>
                <Input type="number" value={discountForm.max_uses} onChange={e => setDiscountForm({...discountForm, max_uses: e.target.value})} placeholder="100" />
              </div>
              <div className="space-y-2">
                <Label>Valid Until</Label>
                <Input type="date" value={discountForm.valid_until} onChange={e => setDiscountForm({...discountForm, valid_until: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountModal(false)}>Cancel</Button>
            <Button onClick={saveDiscountCode} disabled={!discountForm.code} className="bg-[#E11D48] hover:bg-[#E11D48]/90">Create Code</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

            {/* Blog Tab */}
            {activeTab === 'blog' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Blog Management</h2>
                    <p className="text-sm text-muted-foreground mt-1">{blogPosts.length} posts · {blogComments.filter(c => !c.is_approved).length} pending comments</p>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex border rounded-lg overflow-hidden">
                      {['posts','comments'].map(t => (
                        <button key={t} onClick={() => setBlogTab(t)} className={`px-4 py-2 text-sm font-medium ${blogTab===t?'bg-[#003366] text-white':'bg-white text-muted-foreground hover:bg-gray-50'}`}>
                          {t.charAt(0).toUpperCase()+t.slice(1)}
                        </button>
                      ))}
                    </div>
                    {blogTab==='posts' && (
                      <Button onClick={()=>{setEditingPost(null);setBlogForm({title:'',content:'',excerpt:'',category:'general',tags:'',status:'draft'});setShowBlogModal(true);}} className="bg-[#E11D48] hover:bg-[#E11D48]/90">
                        <Plus className="w-4 h-4 mr-2"/>New Post
                      </Button>
                    )}
                  </div>
                </div>
                {blogTab==='posts' && (
                  <div className="space-y-3">
                    {blogPosts.length===0 ? (
                      <Card><CardContent className="py-12 text-center"><Newspaper className="w-12 h-12 text-muted-foreground mx-auto mb-4"/><p className="text-muted-foreground">No blog posts yet</p></CardContent></Card>
                    ) : blogPosts.map(post=>(
                      <Card key={post.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={post.status==='published'?'bg-emerald-100 text-emerald-700':'bg-amber-100 text-amber-700'}>{post.status}</Badge>
                                <Badge variant="outline" className="text-xs">{post.category}</Badge>
                              </div>
                              <h3 className="font-semibold">{post.title}</h3>
                              <p className="text-xs text-muted-foreground mt-1">{post.author_name} · {post.created_at?.slice?.(0,10)} · {post.views||0} views</p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button variant="ghost" size="icon" onClick={()=>{setEditingPost(post);setBlogForm({title:post.title,content:post.content||'',excerpt:post.excerpt||'',category:post.category,tags:(post.tags||[]).join(', '),status:post.status});setShowBlogModal(true);}}>
                                <Edit className="w-4 h-4"/>
                              </Button>
                              <Button variant="ghost" size="icon" onClick={()=>deleteBlogPost(post.id)} className="text-red-500"><Trash2 className="w-4 h-4"/></Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                {blogTab==='comments' && (
                  <div className="space-y-3">
                    {blogComments.length===0 ? (
                      <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground">No comments yet</p></CardContent></Card>
                    ) : blogComments.map(comment=>(
                      <Card key={comment.id} className={comment.is_approved?'':'border-amber-200 bg-amber-50'}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              {!comment.is_approved&&<Badge className="bg-amber-100 text-amber-700 mb-2">Pending</Badge>}
                              <p className="font-medium text-sm">{comment.author_name}</p>
                              <p className="text-sm text-muted-foreground mt-1">{comment.content}</p>
                            </div>
                            <div className="flex gap-2">
                              {!comment.is_approved&&<Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={()=>approveComment(comment.id)}><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Approve</Button>}
                              <Button size="sm" variant="destructive" onClick={()=>deleteComment(comment.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                {/* Blog Post Modal */}
                <Dialog open={showBlogModal} onOpenChange={setShowBlogModal}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editingPost?'Edit Post':'New Blog Post'}</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2"><Label>Title</Label><Input value={blogForm.title} onChange={e=>setBlogForm(f=>({...f,title:e.target.value}))} placeholder="Post title..."/></div>
                      <div className="space-y-2"><Label>Excerpt</Label><Input value={blogForm.excerpt} onChange={e=>setBlogForm(f=>({...f,excerpt:e.target.value}))} placeholder="Short summary..."/></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select value={blogForm.category} onValueChange={v=>setBlogForm(f=>({...f,category:v}))}>
                            <SelectTrigger><SelectValue/></SelectTrigger>
                            <SelectContent>
                              {['general','career-advice','industry-news','employer-tips','student-guide','recruitment'].map(c=>(
                                <SelectItem key={c} value={c}>{c.replace(/-/g,' ')}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select value={blogForm.status} onValueChange={v=>setBlogForm(f=>({...f,status:v}))}>
                            <SelectTrigger><SelectValue/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="published">Published</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2"><Label>Tags (comma separated)</Label><Input value={blogForm.tags} onChange={e=>setBlogForm(f=>({...f,tags:e.target.value}))} placeholder="career, jobs, tips"/></div>
                      <div className="space-y-2"><Label>Content (HTML supported)</Label><Textarea value={blogForm.content} onChange={e=>setBlogForm(f=>({...f,content:e.target.value}))} rows={8} placeholder="Write post content here... HTML is supported." className="font-mono text-sm"/></div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={()=>setShowBlogModal(false)}>Cancel</Button>
                      <Button onClick={saveBlogPost} className="bg-[#003366]">{editingPost?'Update Post':'Publish Post'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {/* Branding Tab */}
            {activeTab === 'branding' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">White-Label Branding</h2>
                  <p className="text-sm text-muted-foreground mt-1">Customise platform appearance, name, and contact details</p>
                </div>
                {loading && !branding && <Skeleton className="h-40" />}
                {(branding || !loading) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader><CardTitle>Platform Identity</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2"><Label>Platform Name</Label><Input value={brandingForm.platform_name||''} onChange={e=>setBrandingForm(f=>({...f,platform_name:e.target.value}))}/></div>
                        <div className="space-y-2"><Label>Tagline</Label><Input value={brandingForm.tagline||''} onChange={e=>setBrandingForm(f=>({...f,tagline:e.target.value}))}/></div>
                        <div className="space-y-2">
                          <Label>Logo</Label>
                          {brandingForm.logo_url && (
                            <div className="mb-2 p-2 border rounded-lg bg-gray-50 inline-block">
                              <img src={brandingForm.logo_url} alt="Current logo" className="h-12 object-contain" onError={e=>{e.target.style.display='none'}} />
                            </div>
                          )}
                          <label className="block">
                            <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm hover:bg-gray-50 ${uploadingLogo?'opacity-50 pointer-events-none':''}`}>
                              {uploadingLogo ? <><span className="animate-spin">⏳</span> Uploading…</> : <>📁 Upload Logo File</>}
                            </div>
                            <input type="file" accept="image/*" className="hidden" onChange={e=>uploadLogo(e.target.files[0])} />
                          </label>
                          <p className="text-xs text-muted-foreground">Or paste a URL:</p>
                          <Input value={brandingForm.logo_url||''} onChange={e=>setBrandingForm(f=>({...f,logo_url:e.target.value}))} placeholder="https://..."/>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Colours</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Primary Colour</Label>
                          <div className="flex gap-3 items-center">
                            <input type="color" value={brandingForm.primary_color||'#003366'} onChange={e=>setBrandingForm(f=>({...f,primary_color:e.target.value}))} className="w-10 h-10 rounded border cursor-pointer"/>
                            <Input value={brandingForm.primary_color||''} onChange={e=>setBrandingForm(f=>({...f,primary_color:e.target.value}))} className="font-mono"/>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Secondary Colour</Label>
                          <div className="flex gap-3 items-center">
                            <input type="color" value={brandingForm.secondary_color||'#E11D48'} onChange={e=>setBrandingForm(f=>({...f,secondary_color:e.target.value}))} className="w-10 h-10 rounded border cursor-pointer"/>
                            <Input value={brandingForm.secondary_color||''} onChange={e=>setBrandingForm(f=>({...f,secondary_color:e.target.value}))} className="font-mono"/>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Font Family</Label>
                          <Select value={brandingForm.font_family||'Inter'} onValueChange={v=>setBrandingForm(f=>({...f,font_family:v}))}>
                            <SelectTrigger><SelectValue/></SelectTrigger>
                            <SelectContent>
                              {['Inter','Roboto','Open Sans','Lato','Poppins','Nunito'].map(font=>(
                                <SelectItem key={font} value={font}>{font}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2"><Label>Support Email</Label><Input type="email" value={brandingForm.contact_email||''} onChange={e=>setBrandingForm(f=>({...f,contact_email:e.target.value}))}/></div>
                        <div className="space-y-2"><Label>Phone (main contact)</Label><Input value={brandingForm.contact_phone||''} onChange={e=>setBrandingForm(f=>({...f,contact_phone:e.target.value}))}/></div>
                        <div className="space-y-2">
                          <Label>Sales Phone Numbers</Label>
                          <p className="text-xs text-muted-foreground">Shown on the Recruiter page and in the footer. Add as many as you need.</p>
                          {(brandingForm.sales_phones||[]).map((ph,i)=>(
                            <div key={i} className="flex gap-2">
                              <Input value={ph} placeholder="+44 ..." onChange={e=>setBrandingForm(f=>({...f,sales_phones:(f.sales_phones||[]).map((x,idx)=>idx===i?e.target.value:x)}))}/>
                              <Button type="button" variant="outline" size="sm" className="text-red-600" onClick={()=>setBrandingForm(f=>({...f,sales_phones:(f.sales_phones||[]).filter((_,idx)=>idx!==i)}))}><Trash2 className="w-4 h-4"/></Button>
                            </div>
                          ))}
                          <Button type="button" variant="outline" size="sm" onClick={()=>setBrandingForm(f=>({...f,sales_phones:[...(f.sales_phones||[]),'']}))}><Plus className="w-4 h-4 mr-2"/>Add sales number</Button>
                        </div>
                        <div className="space-y-2"><Label>Address</Label><Textarea value={brandingForm.address||''} onChange={e=>setBrandingForm(f=>({...f,address:e.target.value}))} rows={3}/></div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Social Media</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2"><Label>LinkedIn</Label><Input value={brandingForm.social_linkedin||''} onChange={e=>setBrandingForm(f=>({...f,social_linkedin:e.target.value}))}/></div>
                        <div className="space-y-2"><Label>Twitter / X</Label><Input value={brandingForm.social_twitter||''} onChange={e=>setBrandingForm(f=>({...f,social_twitter:e.target.value}))}/></div>
                        <div className="space-y-2"><Label>Facebook</Label><Input value={brandingForm.social_facebook||''} onChange={e=>setBrandingForm(f=>({...f,social_facebook:e.target.value}))}/></div>
                        <div className="space-y-2"><Label>YouTube</Label><Input value={brandingForm.social_youtube||''} onChange={e=>setBrandingForm(f=>({...f,social_youtube:e.target.value}))}/></div>
                      </CardContent>
                    </Card>
                    <Card className="md:col-span-2">
                      <CardHeader><CardTitle>Custom CSS</CardTitle></CardHeader>
                      <CardContent>
                        <Textarea value={brandingForm.custom_css||''} onChange={e=>setBrandingForm(f=>({...f,custom_css:e.target.value}))} rows={6} className="font-mono text-sm" placeholder="/* Add custom CSS here */"/>
                      </CardContent>
                    </Card>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button onClick={saveBranding} disabled={savingBranding} className="bg-[#003366] hover:bg-[#003366]/90 px-8">
                    {savingBranding?<Loader2 className="w-4 h-4 animate-spin mr-2"/>:null}Save Branding
                  </Button>
                </div>
              </div>
            )}

            {/* Privacy Policy Tab */}
            {activeTab === 'legal_pages' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">Privacy Policy</h2>
                  <p className="text-sm text-muted-foreground mt-1">Edit the content of the public Privacy Policy page. Leave a blank line between paragraphs. Sections are numbered automatically.</p>
                </div>
                <Card>
                  <CardContent className="pt-6 grid gap-4 md:grid-cols-2">
                    <div className="space-y-2"><Label>Page Title</Label><Input value={privacyForm.title||''} onChange={e=>setPrivacyForm(f=>({...f,title:e.target.value}))}/></div>
                    <div className="space-y-2"><Label>Last Updated</Label><Input value={privacyForm.last_updated||''} onChange={e=>setPrivacyForm(f=>({...f,last_updated:e.target.value}))} placeholder="e.g. 16 March 2026"/></div>
                  </CardContent>
                </Card>
                {privacyForm.sections.map((sec, i) => (
                  <Card key={i}>
                    <CardContent className="pt-6 space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-muted-foreground w-6">{i + 1}.</span>
                        <Input value={sec.heading} onChange={e=>updatePrivacySection(i,{heading:e.target.value})} placeholder="Section heading"/>
                        <Button variant="outline" size="sm" onClick={()=>movePrivacySection(i,-1)} disabled={i===0}>↑</Button>
                        <Button variant="outline" size="sm" onClick={()=>movePrivacySection(i,1)} disabled={i===privacyForm.sections.length-1}>↓</Button>
                        <Button variant="outline" size="sm" className="text-red-600" onClick={()=>setPrivacyForm(f=>({...f,sections:f.sections.filter((_,idx)=>idx!==i)}))}><Trash2 className="w-4 h-4"/></Button>
                      </div>
                      <Textarea value={sec.body} onChange={e=>updatePrivacySection(i,{body:e.target.value})} rows={8} placeholder="Section text"/>
                    </CardContent>
                  </Card>
                ))}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={()=>setPrivacyForm(f=>({...f,sections:[...f.sections,{heading:'',body:''}]}))}><Plus className="w-4 h-4 mr-2"/>Add Section</Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={()=>{ if (window.confirm('Reset the editor to the original default text? (Not saved until you click Save.)')) setPrivacyForm(DEFAULT_PRIVACY); }}>Reset to Default</Button>
                    <Button onClick={savePrivacy} disabled={savingPrivacy} className="bg-[#003366] hover:bg-[#003366]/90 px-8">
                      {savingPrivacy?<Loader2 className="w-4 h-4 animate-spin mr-2"/>:null}Save Privacy Policy
                    </Button>
                  </div>
                </div>
                <LegalContentEditor />
              </div>
            )}

            {activeTab === 'email' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">Email Notifications</h2>
                  <p className="text-sm text-muted-foreground mt-1">Configure email providers, test SMTP, and send job alert digests</p>
                </div>

                {/* Provider status */}
                <Card>
                  <CardHeader><CardTitle>Email Provider Status</CardTitle><CardDescription>SendGrid/Mailgun via environment variables; SMTP can be set below or via environment variables</CardDescription></CardHeader>
                  <CardContent>
                    {emailConfig ? (
                      <div className="space-y-3">
                        {[
                          { name: 'SendGrid', key: 'sendgrid', env: 'SENDGRID_API_KEY' },
                          { name: 'Mailgun', key: 'mailgun', env: 'MAILGUN_API_KEY + MAILGUN_DOMAIN' },
                          { name: 'SMTP', key: 'smtp', env: 'SMTP_HOST + SMTP_USER + SMTP_PASS' },
                        ].map(p => (
                          <div key={p.key} className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{p.env}</p>
                            </div>
                            <Badge className={emailConfig[p.key] ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                              {emailConfig[p.key] ? 'Configured ✓' : 'Not set'}
                            </Badge>
                          </div>
                        ))}
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg text-xs text-blue-700 border border-blue-100">
                          Sending as: <strong>{emailConfig.from_name}</strong> &lt;{emailConfig.from_email}&gt;
                          <span className="ml-2 text-blue-500">(FROM_NAME / FROM_EMAIL env vars)</span>
                        </div>
                        {!emailConfig.sendgrid && !emailConfig.mailgun && !emailConfig.smtp && (
                          <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700 border border-amber-200">
                            ⚠ No email provider configured. Emails will be skipped. Add one of the env vars above to enable sending.
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Loading config...</p>
                    )}
                  </CardContent>
                </Card>

                {/* SMTP Configuration */}
                <Card>
                  <CardHeader>
                    <CardTitle>SMTP Configuration</CardTitle>
                    <CardDescription>
                      Set SMTP credentials here to send mail without editing the server's .env file.
                      {smtpForm.source === 'database' && <span className="ml-1 text-emerald-600 font-medium">Currently using settings saved here.</span>}
                      {smtpForm.source === 'env' && <span className="ml-1 text-amber-600 font-medium">Currently using SMTP_* environment variables — save below to switch to this form.</span>}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">SMTP Host *</Label>
                        <Input value={smtpForm.smtp_host} onChange={e => setSmtpForm({...smtpForm, smtp_host: e.target.value})} placeholder="smtp.gmail.com" />
                      </div>
                      <div>
                        <Label className="text-xs">SMTP Port</Label>
                        <Input type="number" value={smtpForm.smtp_port} onChange={e => setSmtpForm({...smtpForm, smtp_port: e.target.value})} placeholder="587" />
                      </div>
                      <div>
                        <Label className="text-xs">SMTP Username *</Label>
                        <Input value={smtpForm.smtp_user} onChange={e => setSmtpForm({...smtpForm, smtp_user: e.target.value})} placeholder="your@email.com" />
                      </div>
                      <div>
                        <Label className="text-xs">SMTP Password {smtpForm.has_password && <span className="text-muted-foreground font-normal">(leave blank to keep current)</span>}</Label>
                        <Input type="password" value={smtpForm.smtp_pass} onChange={e => setSmtpForm({...smtpForm, smtp_pass: e.target.value})} placeholder={smtpForm.has_password ? '••••••••' : 'App password or SMTP password'} />
                      </div>
                      <div>
                        <Label className="text-xs">From Email</Label>
                        <Input value={smtpForm.from_email} onChange={e => setSmtpForm({...smtpForm, from_email: e.target.value})} placeholder="noreply@yourdomain.com" />
                      </div>
                      <div>
                        <Label className="text-xs">From Name</Label>
                        <Input value={smtpForm.from_name} onChange={e => setSmtpForm({...smtpForm, from_name: e.target.value})} placeholder="WorksRun" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <Button variant="outline" onClick={clearSmtpSettings} disabled={clearingSmtp || smtpForm.source !== 'database'}>
                        {clearingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
                        Clear Saved Settings
                      </Button>
                      <Button onClick={saveSmtpSettings} disabled={savingSmtp} className="bg-[#003366] hover:bg-[#003366]/90 text-white">
                        {savingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
                        Save SMTP Settings
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* SMTP Testing */}
                {emailConfig?.smtp && (
                  <Card>
                    <CardHeader><CardTitle>SMTP Testing</CardTitle><CardDescription>Test your SMTP configuration and send test emails</CardDescription></CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">Test SMTP Connection</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Verify SMTP server is reachable and credentials are valid</p>
                        </div>
                        <Button onClick={testSmtpConnection} disabled={testingSmtp} className="bg-[#003366] hover:bg-[#003366]/90 text-white shrink-0">
                          {testingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
                          Test Connection
                        </Button>
                      </div>
                      {smtpTestResult && (
                        <div className={`p-3 rounded-lg border ${smtpTestResult.status === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                          <p className="text-sm font-medium">{smtpTestResult.status === 'ok' ? '✓ Connection successful' : '✗ Connection failed'}</p>
                          <p className="text-xs mt-1">{smtpTestResult.message}</p>
                        </div>
                      )}
                      <div className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">Send Test Email</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Send yourself a test email from any template</p>
                        </div>
                        <Button onClick={() => setShowTestEmailModal(true)} className="bg-[#003366] hover:bg-[#003366]/90 text-white shrink-0">
                          Send Test Email
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Manual triggers */}
                <Card>
                  <CardHeader><CardTitle>Send Job Alert Emails</CardTitle><CardDescription>Manually trigger alert emails — instant alerts fire automatically on job creation</CardDescription></CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { type: 'instant', label: 'Instant Alerts', desc: 'Send to all users with instant-frequency alerts, matched against all active jobs', color: 'bg-[#003366] hover:bg-[#003366]/90' },
                      { type: 'daily', label: 'Daily Digest', desc: 'Send digest email to all users with daily-frequency alerts', color: 'bg-emerald-600 hover:bg-emerald-700' },
                      { type: 'weekly', label: 'Weekly Digest', desc: 'Send digest email to all users with weekly-frequency alerts', color: 'bg-purple-600 hover:bg-purple-700' },
                    ].map(({ type, label, desc, color }) => (
                      <div key={type} className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">{label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                        </div>
                        <Button onClick={() => sendDigest(type)} disabled={!!sendingDigest} className={`${color} text-white shrink-0 ml-4`}>
                          {sendingDigest === type ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Send className="w-4 h-4 mr-2"/>}
                          Send Now
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Setup guide */}
                <Card>
                  <CardHeader><CardTitle>Setup Guide</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-4 text-sm">
                      {[
                        { provider: 'SendGrid (recommended)', vars: [['SENDGRID_API_KEY', 'Your SendGrid API key']] },
                        { provider: 'Mailgun', vars: [['MAILGUN_API_KEY', 'Your Mailgun API key'], ['MAILGUN_DOMAIN', 'e.g. mg.yourdomain.com']] },
                        { provider: 'SMTP (Gmail, Outlook, custom)', vars: [['SMTP_HOST', 'e.g. smtp.gmail.com'], ['SMTP_PORT', '587 (TLS) or 465 (SSL)'], ['SMTP_USER', 'your@email.com'], ['SMTP_PASS', 'App password or SMTP password']] },
                      ].map(({ provider, vars }) => (
                        <div key={provider}>
                          <p className="font-semibold mb-1">{provider}</p>
                          <div className="bg-gray-50 rounded p-3 space-y-1">
                            {vars.map(([k, v]) => (
                              <p key={k} className="font-mono text-xs"><span className="text-[#003366]">{k}</span>=<span className="text-gray-500">{v}</span></p>
                            ))}
                          </div>
                        </div>
                      ))}
                      <p className="text-muted-foreground text-xs mt-2">Add these to your <code className="bg-gray-100 px-1 rounded">.env</code> file in the backend directory. The first configured provider is used.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'skills_tests' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div><h2 className="text-xl font-semibold">Skills Tests</h2><p className="text-sm text-muted-foreground mt-1">{adminTests.length} tests configured</p></div>
                  <Button onClick={() => { setEditingTest(null); setTestForm({ title:'', description:'', category:'general', difficulty:'intermediate', duration_minutes:15, passing_score:70, is_active:true, questions:[] }); setShowTestModal(true); }} className="bg-[#E11D48] hover:bg-[#E11D48]/90"><Plus className="w-4 h-4 mr-2"/>New Test</Button>
                </div>
                <div className="space-y-3">
                  {adminTests.length === 0 ? (
                    <Card><CardContent className="py-12 text-center"><CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-4"/><p className="text-muted-foreground">No tests yet. Create one or they will be AI-generated on demand.</p></CardContent></Card>
                  ) : adminTests.map(test => (
                    <Card key={test.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={test.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>{test.is_active ? 'Active' : 'Inactive'}</Badge>
                              <Badge variant="outline">{test.difficulty}</Badge>
                              <Badge variant="outline">{test.category}</Badge>
                            </div>
                            <h3 className="font-semibold">{test.title}</h3>
                            <p className="text-xs text-muted-foreground mt-1">{test.description} · {test.duration_minutes}min · {test.questions?.length || 0} questions · Pass: {test.passing_score}%</p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button variant="ghost" size="icon" onClick={() => { setEditingTest(test); setTestForm({ title:test.title, description:test.description||'', category:test.category, difficulty:test.difficulty, duration_minutes:test.duration_minutes, passing_score:test.passing_score, is_active:test.is_active, questions:test.questions||[] }); setShowTestModal(true); }}><Edit className="w-4 h-4"/></Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteTest(test.id)} className="text-red-500"><Trash2 className="w-4 h-4"/></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Test Modal */}
                <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
                  <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editingTest ? 'Edit Test' : 'Create Skills Test'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 space-y-2"><Label>Title *</Label><Input value={testForm.title} onChange={e=>setTestForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Python Fundamentals"/></div>
                        <div className="col-span-2 space-y-2"><Label>Description</Label><Textarea value={testForm.description} onChange={e=>setTestForm(f=>({...f,description:e.target.value}))} rows={2}/></div>
                        <div className="space-y-2"><Label>Category</Label>
                          <Select value={testForm.category} onValueChange={v=>setTestForm(f=>({...f,category:v}))}>
                            <SelectTrigger><SelectValue/></SelectTrigger>
                            <SelectContent>{['general','programming','data','marketing','management','communication','finance'].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2"><Label>Difficulty</Label>
                          <Select value={testForm.difficulty} onValueChange={v=>setTestForm(f=>({...f,difficulty:v}))}>
                            <SelectTrigger><SelectValue/></SelectTrigger>
                            <SelectContent>{['beginner','intermediate','advanced'].map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2"><Label>Duration (minutes)</Label><Input type="number" value={testForm.duration_minutes} onChange={e=>setTestForm(f=>({...f,duration_minutes:e.target.value}))}/></div>
                        <div className="space-y-2"><Label>Passing Score (%)</Label><Input type="number" value={testForm.passing_score} onChange={e=>setTestForm(f=>({...f,passing_score:e.target.value}))}/></div>
                      </div>
                      <div className="flex items-center gap-2"><Switch checked={testForm.is_active} onCheckedChange={v=>setTestForm(f=>({...f,is_active:v}))}/><Label>Active</Label></div>

                      {/* Questions */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-3">Questions ({testForm.questions.length})</h4>
                        {testForm.questions.map((q,idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg mb-2 text-sm">
                            <div className="flex justify-between"><p className="font-medium">{idx+1}. {q.question}</p><button onClick={()=>removeQuestion(idx)} className="text-red-500 text-xs">Remove</button></div>
                            <div className="mt-1 space-y-0.5">{q.options?.map((opt,oi)=><p key={oi} className={oi===q.correct_answer?'text-emerald-600 font-medium':'text-gray-500'}>{'ABCD'[oi]}. {opt}{oi===q.correct_answer?' ✓':''}</p>)}</div>
                          </div>
                        ))}
                        <div className="border rounded-lg p-4 space-y-3 bg-blue-50">
                          <p className="text-sm font-semibold">Add Question</p>
                          <Input value={questionForm.question} onChange={e=>setQuestionForm(f=>({...f,question:e.target.value}))} placeholder="Question text"/>
                          {[0,1,2,3].map(i=>(
                            <div key={i} className="flex items-center gap-2">
                              <input type="radio" name="correct" checked={questionForm.correct_answer===i} onChange={()=>setQuestionForm(f=>({...f,correct_answer:i}))}/>
                              <Input value={questionForm.options[i]||''} onChange={e=>{const opts=[...questionForm.options];opts[i]=e.target.value;setQuestionForm(f=>({...f,options:opts}));}} placeholder={`Option ${'ABCD'[i]}`}/>
                            </div>
                          ))}
                          <Input value={questionForm.explanation} onChange={e=>setQuestionForm(f=>({...f,explanation:e.target.value}))} placeholder="Explanation (optional)"/>
                          <Button type="button" onClick={addQuestion} variant="outline" size="sm"><Plus className="w-3 h-3 mr-1"/>Add Question</Button>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={()=>setShowTestModal(false)}>Cancel</Button>
                      <Button onClick={saveTest} className="bg-[#003366] hover:bg-[#003366]/90">Save Test</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {activeTab === 'cms' && (
              <div className="space-y-6">
                <div><h2 className="text-xl font-semibold">Content Management</h2><p className="text-sm text-muted-foreground mt-1">Manage site content, career tips and homepage sections</p></div>
                <div className="flex border rounded-lg overflow-hidden w-fit">
                  {['career_tips','career_topics','homepage'].map(t=>(
                    <button key={t} onClick={()=>setCmsTab(t)} className={`px-4 py-2 text-sm font-medium ${cmsTab===t?'bg-[#003366] text-white':'bg-white text-muted-foreground hover:bg-gray-50'}`}>
                      {t.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                    </button>
                  ))}
                </div>

                {cmsTab === 'career_tips' && (
                  <Card>
                    <CardHeader><CardTitle>Career Tips</CardTitle><CardDescription>Quick tips shown on the Career Advice page. One tip per line.</CardDescription></CardHeader>
                    <CardContent className="space-y-4">
                      <Textarea
                        rows={12}
                        value={(cmsContent.career_tips?.items || []).join('\n')}
                        onChange={e => setCmsContent(c => ({ ...c, career_tips: { ...c.career_tips, items: e.target.value.split('\n') } }))}
                        placeholder={"Tailor your CV for every role\nPractise STAR method answers\nResearch the company thoroughly"}
                      />
                      <Button onClick={() => saveCmsSection('career_tips')} disabled={savingCms} className="bg-[#003366] hover:bg-[#003366]/90">
                        {savingCms ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}Save Tips
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {cmsTab === 'career_topics' && (
                  <Card>
                    <CardHeader><CardTitle>Career Topics</CardTitle><CardDescription>Featured topic cards. Format: Title|Description (one per line)</CardDescription></CardHeader>
                    <CardContent className="space-y-4">
                      <Textarea
                        rows={10}
                        value={(cmsContent.career_topics?.items || []).map(t => `${t.title}|${t.description}`).join('\n')}
                        onChange={e => setCmsContent(c => ({ ...c, career_topics: { ...c.career_topics, items: e.target.value.split('\n').filter(Boolean).map(l => { const [title,...rest]=l.split('|'); return {title:title.trim(),description:rest.join('|').trim()}; }) } }))}
                        placeholder={"CV Writing Tips|Expert advice on crafting a standout CV\nInterview Preparation|Master behavioural and technical interviews"}
                      />
                      <Button onClick={() => saveCmsSection('career_topics')} disabled={savingCms} className="bg-[#003366] hover:bg-[#003366]/90">
                        {savingCms ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}Save Topics
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {cmsTab === 'homepage' && (
                  <Card>
                    <CardHeader><CardTitle>Homepage Hero</CardTitle><CardDescription>Edit the main homepage headline and call-to-action text</CardDescription></CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2"><Label>Hero Title</Label><Input value={cmsContent.homepage?.hero_title||''} onChange={e=>setCmsContent(c=>({...c,homepage:{...c.homepage,hero_title:e.target.value}}))} placeholder="Find Your Dream Job in the UK"/></div>
                      <div className="space-y-2"><Label>Hero Subtitle</Label><Textarea value={cmsContent.homepage?.hero_subtitle||''} onChange={e=>setCmsContent(c=>({...c,homepage:{...c.homepage,hero_subtitle:e.target.value}}))} rows={2} placeholder="Connect with top employers..."/></div>
                      <div className="space-y-2"><Label>Primary CTA Text</Label><Input value={cmsContent.homepage?.cta_primary||''} onChange={e=>setCmsContent(c=>({...c,homepage:{...c.homepage,cta_primary:e.target.value}}))} placeholder="Search Jobs"/></div>
                      <div className="space-y-2"><Label>Secondary CTA Text</Label><Input value={cmsContent.homepage?.cta_secondary||''} onChange={e=>setCmsContent(c=>({...c,homepage:{...c.homepage,cta_secondary:e.target.value}}))} placeholder="Post a Job"/></div>
                      <Button onClick={() => saveCmsSection('homepage')} disabled={savingCms} className="bg-[#003366] hover:bg-[#003366]/90">
                        {savingCms ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}Save Homepage Content
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'email_templates' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold">Email Templates</h2>
                  <p className="text-sm text-muted-foreground mt-1">Customize the HTML/text for system emails</p>
                </div>

                {/* Template variables reference */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-base">Available Template Variables</CardTitle>
                    <CardDescription>Use these in your subject and HTML body</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {[
                        ['{{name}}', 'User full name'],
                        ['{{count}}', 'Number of matching items'],
                        ['{{alert_name}}', 'Job alert name'],
                        ['{{job_title}}', 'Job position title'],
                        ['{{company}}', 'Company name'],
                        ['{{status}}', 'Application status'],
                        ['{{amount}}', 'Payment amount'],
                        ['{{plan_name}}', 'Subscription plan'],
                        ['{{transaction_id}}', 'Payment transaction ID'],
                        ['{{renewal_date}}', 'Subscription renewal date'],
                        ['{{expires_at}}', 'Subscription expiration'],
                      ].map(([var_name, desc]) => (
                        <div key={var_name} className="flex items-center gap-2">
                          <code className="bg-white px-2 py-1 rounded text-xs font-mono font-semibold text-[#003366]">{var_name}</code>
                          <span className="text-muted-foreground text-xs">{desc}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {editingTemplate ? (
                  <Card>
                    <CardHeader>
                      <CardTitle>Editing: {editingTemplate.name.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</CardTitle>
                      <CardDescription>Use {'{{variables}}'} for dynamic content</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2"><Label>Subject Line</Label><Input value={templateForm.subject} onChange={e=>setTemplateForm(f=>({...f,subject:e.target.value}))}/></div>
                      <div className="space-y-2"><Label>Preview Text (shown in inbox)</Label><Input value={templateForm.preview_text} onChange={e=>setTemplateForm(f=>({...f,preview_text:e.target.value}))}/></div>
                      <div className="space-y-2"><Label>HTML Body (leave blank to use system default)</Label><Textarea value={templateForm.html_body} onChange={e=>setTemplateForm(f=>({...f,html_body:e.target.value}))} rows={12} placeholder="<p>Hi {{name}},</p>..." className="font-mono text-sm"/></div>
                      <div className="flex gap-2">
                        <Button onClick={saveTemplate} disabled={savingTemplate} className="bg-[#003366] hover:bg-[#003366]/90">{savingTemplate?<Loader2 className="w-4 h-4 animate-spin mr-2"/>:null}Save Template</Button>
                        <Button variant="outline" onClick={()=>setEditingTemplate(null)}>Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {emailTemplates.map(tmpl => (
                      <Card key={tmpl.name}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-sm">{tmpl.name.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</h3>
                                <Badge className={tmpl.is_custom?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-500'}>{tmpl.is_custom?'Customised':'Default'}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{tmpl.preview_text || 'No preview text'}</p>
                              <p className="text-xs text-muted-foreground font-mono mt-1">{tmpl.subject}</p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button variant="outline" size="sm" onClick={()=>{setEditingTemplate(tmpl);setTemplateForm({subject:tmpl.subject||'',html_body:tmpl.html_body||'',preview_text:tmpl.preview_text||''});}}>
                                <Edit className="w-3.5 h-3.5 mr-1"/>Edit
                              </Button>
                              {tmpl.is_custom && <Button variant="ghost" size="sm" onClick={()=>resetTemplate(tmpl.name)} className="text-red-500 hover:text-red-700">Reset</Button>}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-8">

                {/* CAPTCHA Configuration */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#003366]" /> CAPTCHA — Registration Security</CardTitle>
                    <CardDescription>Control whether new users must solve a CAPTCHA challenge before registering. Helps prevent spam and bot sign-ups.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">

                    {/* Enable toggle */}
                    <div className="flex items-center justify-between p-4 border rounded-xl bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-800">Enable CAPTCHA on Registration</p>
                        <p className="text-sm text-muted-foreground mt-0.5">When enabled, users must answer a maths question before creating an account.</p>
                      </div>
                      <button
                        onClick={() => setCaptchaSettings(s => ({ ...s, enabled: !s.enabled }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${captchaSettings.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${captchaSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* Difficulty */}
                    <div className="space-y-3">
                      <p className="font-semibold text-gray-800">Difficulty Level</p>
                      <p className="text-sm text-muted-foreground">Controls the number range used in the arithmetic question.</p>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'easy', label: 'Easy', desc: 'Numbers 1–5 (addition only)' },
                          { value: 'medium', label: 'Medium', desc: 'Numbers 1–10 (+ − ×)' },
                          { value: 'hard', label: 'Hard', desc: 'Numbers 1–20 (+ − ×)' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => setCaptchaSettings(s => ({ ...s, difficulty: opt.value }))}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${captchaSettings.difficulty === opt.value ? 'border-[#003366] bg-[#003366]/5' : 'border-gray-200 hover:border-gray-300'}`}
                          >
                            <p className={`font-semibold text-sm ${captchaSettings.difficulty === opt.value ? 'text-[#003366]' : 'text-gray-700'}`}>{opt.label}</p>
                            <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-4 border rounded-xl bg-blue-50 space-y-2">
                      <p className="text-sm font-semibold text-blue-800">Preview — what registering users will see:</p>
                      {captchaSettings.enabled ? (
                        <div className="flex items-center gap-3 bg-white border rounded-lg px-4 py-3 w-fit">
                          <span className="font-mono font-semibold text-[#003366]">
                            {captchaSettings.difficulty === 'easy' ? 'What is 3 + 2?' : captchaSettings.difficulty === 'hard' ? 'What is 14 × 3?' : 'What is 7 + 5?'}
                          </span>
                          <div className="w-20 h-9 border rounded-md bg-gray-50 flex items-center justify-center text-xs text-muted-foreground">Answer</div>
                          <span className="text-xs text-[#003366] underline">New question</span>
                        </div>
                      ) : (
                        <p className="text-sm text-blue-700">CAPTCHA is <strong>disabled</strong> — no challenge shown on the registration form.</p>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={saveCaptchaSettings} disabled={savingCaptcha} className="bg-[#003366] hover:bg-[#003366]/90">
                        {savingCaptcha ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/>Saving…</> : 'Save Settings'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Email Verification info card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#003366]" /> Email Verification</CardTitle>
                    <CardDescription>Controls how new accounts are verified to prevent spam registrations.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold text-emerald-800 text-sm">Email verification is active</p>
                        <p className="text-sm text-emerald-700 mt-1">
                          Every new user receives a verification email immediately after registration. An amber banner prompts unverified users to verify their address.
                          The verification link expires after <strong>24 hours</strong>.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {[
                        { icon: '📧', title: 'Verification email sent on register', detail: 'Automatically triggered when a new account is created.' },
                        { icon: '🔗', title: 'Secure token link', detail: 'Each link is unique, single-use, and expires in 24 hours.' },
                        { icon: '🔄', title: 'Resend from the banner', detail: 'Users can request a new link using the amber banner at the top of the page.' },
                        { icon: '✅', title: 'Email Templates', detail: 'Customise the verification email body in Admin → Email Templates.' },
                      ].map(({ icon, title, detail }) => (
                        <div key={title} className="flex gap-3 p-3 border rounded-lg">
                          <span className="text-xl">{icon}</span>
                          <div>
                            <p className="font-medium text-gray-800">{title}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

              </div>
            )}

          </div>
        </main>
      </div>

      {/* Test Email Modal */}
      <Dialog open={showTestEmailModal} onOpenChange={setShowTestEmailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>Send yourself a test email to verify templates work correctly</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Email Address</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="your@example.com"
                value={testEmailForm.to_email}
                onChange={(e) => setTestEmailForm(f => ({...f, to_email: e.target.value}))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-template">Template</Label>
              <select
                id="test-template"
                value={testEmailForm.template}
                onChange={(e) => setTestEmailForm(f => ({...f, template: e.target.value}))}
                className="w-full px-3 py-2 border rounded-md bg-white text-sm"
              >
                <optgroup label="Account Emails">
                  <option value="welcome">Welcome</option>
                  <option value="registration">Registration / Email Verification</option>
                  <option value="login">Login Notification</option>
                  <option value="forgot_password">Forgot Password</option>
                </optgroup>
                <optgroup label="Payment Emails">
                  <option value="payment_confirmation">Payment Confirmation</option>
                  <option value="renewal_reminder">Renewal Reminder</option>
                  <option value="renewal_confirmation">Renewal Confirmation</option>
                </optgroup>
                <optgroup label="Application Emails">
                  <option value="application_received">Application Received</option>
                </optgroup>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestEmailModal(false)}>Cancel</Button>
            <Button onClick={sendTestEmail} disabled={sendingTestEmail} className="bg-[#003366] hover:bg-[#003366]/90">
              {sendingTestEmail ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/>Sending...</> : 'Send Test Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Country Modal */}
      <Dialog open={showCountryModal} onOpenChange={setShowCountryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCountry ? 'Edit Country' : 'Add Country'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Country Name</Label>
              <Input placeholder="e.g., United Kingdom" value={countryForm.name} onChange={e => setCountryForm({...countryForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Country Code (ISO)</Label>
              <Input placeholder="e.g., GB" value={countryForm.code} onChange={e => setCountryForm({...countryForm, code: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea placeholder="Additional notes..." value={countryForm.description} onChange={e => setCountryForm({...countryForm, description: e.target.value})} rows={3} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <input type="checkbox" checked={countryForm.is_active} onChange={e => setCountryForm({...countryForm, is_active: e.target.checked})} className="w-4 h-4" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCountryModal(false)}>Cancel</Button>
            <Button onClick={saveCountry} className="bg-[#003366] hover:bg-[#003366]/90">Save Country</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regions & Cities Modal */}
      <Dialog open={showLocationsModal} onOpenChange={setShowLocationsModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Regions & Cities — {locationsCountry?.name}</DialogTitle>
            <DialogDescription>Used for location filtering across job search and job posting</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {locationsCountry && regions.length === 0 && !loadingLocations && (
              <div className="p-4 rounded-lg bg-[#003366]/5 border border-[#003366]/20 flex items-center justify-between gap-3">
                <p className="text-sm">One-click import {locationsCountry.name}'s regions/states and cities instead of typing them in.</p>
                <Button size="sm" onClick={autoPopulateLocations} disabled={autoPopulating} className="bg-[#003366] hover:bg-[#003366]/90 shrink-0">
                  {autoPopulating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Auto-populate
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Input
                placeholder="Add a region manually, e.g. Greater London"
                value={newRegionName}
                onChange={e => setNewRegionName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRegion(); } }}
              />
              <Button onClick={addRegion} className="shrink-0 bg-[#003366] hover:bg-[#003366]/90">
                <Plus className="w-4 h-4 mr-1" />Add Region
              </Button>
            </div>

            {loadingLocations ? (
              <Skeleton className="h-32" />
            ) : regions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No regions yet for {locationsCountry?.name}.</p>
            ) : (
              <div className="space-y-3">
                {regions.map(region => (
                  <div key={region.id} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm">{region.name}</p>
                      <Button size="sm" variant="ghost" className="text-red-600 h-7 px-2" onClick={() => deleteRegion(region.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(citiesByRegion[region.id] || []).map(city => (
                        <Badge key={city.id} variant="secondary" className="px-2 py-1">
                          {city.name}
                          <button className="ml-2" onClick={() => deleteCity(city.id)}><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                      {(citiesByRegion[region.id] || []).length === 0 && (
                        <p className="text-xs text-muted-foreground">No cities yet</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Add a city manually, e.g. Manchester"
                        className="h-8 text-sm"
                        value={newCityInputs[region.id] || ''}
                        onChange={e => setNewCityInputs({ ...newCityInputs, [region.id]: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCity(region.id); } }}
                      />
                      <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => addCity(region.id)}>
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLocationsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Modal */}
      <Dialog open={showSubcategoryModal} onOpenChange={setShowSubcategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Subcategories — {subcategoryParentCategory?.name}</DialogTitle>
            <DialogDescription>
              Shown as a hover flyout under this category on the {subcategoryType === 'job' ? 'Jobs' : 'Courses'} browse page
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Add a subcategory, e.g. Frontend Developer"
                value={newSubcategoryName}
                onChange={e => setNewSubcategoryName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubcategory(); } }}
              />
              <Button onClick={addSubcategory} className="shrink-0 bg-[#003366] hover:bg-[#003366]/90">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {loadingSubcategories ? (
              <Skeleton className="h-20" />
            ) : subcategories.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No subcategories yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {subcategories.map(sub => (
                  <Badge key={sub.id} variant="secondary" className="px-2 py-1">
                    {sub.name}
                    <button className="ml-2" onClick={() => deleteSubcategory(sub.id)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubcategoryModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner University Modal */}
      <Dialog open={showPartnerUniModal} onOpenChange={setShowPartnerUniModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPartnerUni ? 'Edit Partner University' : 'Add Partner University'}</DialogTitle>
            <DialogDescription>Shown on the International Students / Study Abroad page</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>University Name</Label>
                <Input placeholder="e.g., University of Manchester" value={partnerUniForm.name} onChange={e => setPartnerUniForm({...partnerUniForm, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input placeholder="e.g., UK" value={partnerUniForm.country} onChange={e => setPartnerUniForm({...partnerUniForm, country: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Website (optional)</Label>
                <Input placeholder="https://..." value={partnerUniForm.website} onChange={e => setPartnerUniForm({...partnerUniForm, website: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Logo URL (optional)</Label>
                <Input placeholder="https://..." value={partnerUniForm.logo_url} onChange={e => setPartnerUniForm({...partnerUniForm, logo_url: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea placeholder="Short description..." value={partnerUniForm.description} onChange={e => setPartnerUniForm({...partnerUniForm, description: e.target.value})} rows={2} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active (visible on the public page)</Label>
              <input type="checkbox" checked={partnerUniForm.is_active} onChange={e => setPartnerUniForm({...partnerUniForm, is_active: e.target.checked})} className="w-4 h-4" />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base">Programs ({partnerUniForm.programs.length})</Label>
                <Button size="sm" variant="outline" onClick={addProgramRow}>
                  <Plus className="w-4 h-4 mr-1" />Add Program
                </Button>
              </div>
              <div className="space-y-4">
                {partnerUniForm.programs.map((prog, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-3 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Program {idx + 1}</span>
                      <Button size="sm" variant="ghost" className="text-red-600 h-7 px-2" onClick={() => removeProgramRow(idx)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Program name, e.g. MSc Data Science" value={prog.name} onChange={e => updateProgramRow(idx, 'name', e.target.value)} />
                      <Select value={prog.degree_level} onValueChange={v => updateProgramRow(idx, 'degree_level', v)}>
                        <SelectTrigger><SelectValue placeholder="Degree level" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="foundation">Foundation</SelectItem>
                          <SelectItem value="undergraduate">Undergraduate</SelectItem>
                          <SelectItem value="postgraduate">Postgraduate</SelectItem>
                          <SelectItem value="phd">PhD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Input placeholder="Duration, e.g. 1 year" value={prog.duration || ''} onChange={e => updateProgramRow(idx, 'duration', e.target.value)} />
                      <Input placeholder="Tuition, e.g. £22,000/year" value={prog.tuition_fee || ''} onChange={e => updateProgramRow(idx, 'tuition_fee', e.target.value)} />
                      <Input placeholder="Subject, e.g. Business" value={prog.category || ''} onChange={e => updateProgramRow(idx, 'category', e.target.value)} />
                    </div>
                    <Input
                      placeholder="Intake months, comma-separated e.g. September, January"
                      value={(prog.intake_dates || []).join(', ')}
                      onChange={e => updateProgramRow(idx, 'intake_dates', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    />
                    <Textarea
                      placeholder="Entry requirements (optional)"
                      value={prog.entry_requirements || ''}
                      onChange={e => updateProgramRow(idx, 'entry_requirements', e.target.value)}
                      rows={2}
                    />
                  </div>
                ))}
                {partnerUniForm.programs.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No programs added yet.</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartnerUniModal(false)}>Cancel</Button>
            <Button onClick={savePartnerUniversity} className="bg-[#003366] hover:bg-[#003366]/90">Save University</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Category Modal */}
      <Dialog open={showJobCategoryModal} onOpenChange={setShowJobCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingJobCategory ? 'Edit Job Category' : 'Add Job Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input placeholder="e.g., Software Development" value={jobCategoryForm.name} onChange={e => setJobCategoryForm({...jobCategoryForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input placeholder="e.g., software-development" value={jobCategoryForm.slug} onChange={e => setJobCategoryForm({...jobCategoryForm, slug: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea placeholder="Category description..." value={jobCategoryForm.description} onChange={e => setJobCategoryForm({...jobCategoryForm, description: e.target.value})} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Icon (optional)</Label>
              <Input placeholder="Icon emoji or name" value={jobCategoryForm.icon} onChange={e => setJobCategoryForm({...jobCategoryForm, icon: e.target.value})} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <input type="checkbox" checked={jobCategoryForm.is_active} onChange={e => setJobCategoryForm({...jobCategoryForm, is_active: e.target.checked})} className="w-4 h-4" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJobCategoryModal(false)}>Cancel</Button>
            <Button onClick={saveJobCategory} className="bg-[#003366] hover:bg-[#003366]/90">Save Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Category Modal */}
      <Dialog open={showCourseCategoryModal} onOpenChange={setShowCourseCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCourseCategory ? 'Edit Course Category' : 'Add Course Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input placeholder="e.g., Web Development" value={courseCategoryForm.name} onChange={e => setCourseCategoryForm({...courseCategoryForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input placeholder="e.g., web-development" value={courseCategoryForm.slug} onChange={e => setCourseCategoryForm({...courseCategoryForm, slug: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea placeholder="Category description..." value={courseCategoryForm.description} onChange={e => setCourseCategoryForm({...courseCategoryForm, description: e.target.value})} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Icon (optional)</Label>
              <Input placeholder="Icon emoji or name" value={courseCategoryForm.icon} onChange={e => setCourseCategoryForm({...courseCategoryForm, icon: e.target.value})} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <input type="checkbox" checked={courseCategoryForm.is_active} onChange={e => setCourseCategoryForm({...courseCategoryForm, is_active: e.target.checked})} className="w-4 h-4" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCourseCategoryModal(false)}>Cancel</Button>
            <Button onClick={saveCourseCategory} className="bg-[#003366] hover:bg-[#003366]/90">Save Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Currency Modal */}
      <Dialog open={showCurrencyModal} onOpenChange={setShowCurrencyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCurrency ? 'Edit Currency' : 'Add Currency'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Currency Code</Label>
              <Input placeholder="e.g., GBP" value={currencyForm.code} onChange={e => setCurrencyForm({...currencyForm, code: e.target.value.toUpperCase()})} disabled={!!editingCurrency} maxLength="3" />
            </div>
            <div className="space-y-2">
              <Label>Currency Name</Label>
              <Input placeholder="e.g., British Pound" value={currencyForm.name} onChange={e => setCurrencyForm({...currencyForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Currency Symbol</Label>
              <Input placeholder="e.g., £" value={currencyForm.symbol} onChange={e => setCurrencyForm({...currencyForm, symbol: e.target.value})} maxLength="5" />
            </div>
            <div className="space-y-2">
              <Label>Symbol Position</Label>
              <select value={currencyForm.position} onChange={e => setCurrencyForm({...currencyForm, position: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="before">Before amount (£500)</option>
                <option value="after">After amount (500 £)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Decimal Places</Label>
              <Input type="number" value={currencyForm.decimal_places} onChange={e => setCurrencyForm({...currencyForm, decimal_places: parseInt(e.target.value)})} min="0" max="4" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <input type="checkbox" checked={currencyForm.is_active} onChange={e => setCurrencyForm({...currencyForm, is_active: e.target.checked})} className="w-4 h-4" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCurrencyModal(false)}>Cancel</Button>
            <Button onClick={saveCurrency} className="bg-[#003366] hover:bg-[#003366]/90">Save Currency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Edit Modal */}
      <Dialog open={showUserEditModal} onOpenChange={setShowUserEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User: {editingEditUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={userEditForm.full_name} onChange={e => setUserEditForm({...userEditForm, full_name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={userEditForm.email} onChange={e => setUserEditForm({...userEditForm, email: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={userEditForm.phone} onChange={e => setUserEditForm({...userEditForm, phone: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={userEditForm.location} onChange={e => setUserEditForm({...userEditForm, location: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea value={userEditForm.bio} onChange={e => setUserEditForm({...userEditForm, bio: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserEditModal(false)}>Cancel</Button>
            <Button onClick={saveUserEdit} className="bg-[#003366] hover:bg-[#003366]/90">Save User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
